/**
 * Centralized, production-grade Internationalization (i18n) Dictionary
 * Languages: English (en), Tamil (ta), Malayalam (ml), Hindi (hi)
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', nativeName: 'English' },
  { code: 'ta', label: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'ml', label: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी' },
];

export const translations = {
  en: {
    common: {
      micro: 'Micro',
      small: 'Small',
      medium: 'Medium',
      enterprises: 'Enterprises',
      businesses: 'Businesses',
      shops: 'Shops',
      all: 'All',
      search: 'Search',
      close: 'Close',
      save: 'Save',
      cancel: 'Cancel',
      reset: 'Reset',
      loading: 'Loading...',
      active: 'Active',
      details: 'Details',
      explore: 'Explore',
      verified: 'Verified',
      status: 'Status',
    },
    nav: {
      home: 'Overview',
      analytics: 'Analytics',
      search: 'Explore',
      trends: 'Trends',
      forecast: 'Outlook',
      profile: 'My Business',
      appTitle: 'MSME Business Insights',
      brandName: 'LYSARQ',
      brandSub: 'MSME',
      searchPlaceholder: 'Search shops, pin codes, trades across India...',
      saveView: 'Save View',
      download: 'Download',
      share: 'Share',
      updatingMarket: 'Updating market data for',
      platformVisitors: 'Platform Visitors',
      languageLabel: 'Language / மொழி / ഭാഷ / भाषा:',
      tools: 'Tools',
      allTools: 'All tools',
      contactUs: 'Contact Us',
      needAi: 'Does your business need AI?',
    },
    visitor: {
      label: 'Total Visitors',
      short: 'Visitors',
      today: "Today's Visitors",
      allTime: 'All-Time Visitors',
      status: 'Verified Counter',
      footer: 'Session-Verified MSME Analytics',
    },
    dashboard: {
      heroHeading1: 'Find New Customers & Grow Sales',
      heroHeading2: 'With Simple, Verified Market Data',
      heroSubheading: 'Find genuine buyers, check nearby competitors, and see where demand is growing',
      heroNarrative: 'Grow your sales with real, verified business data across India. Whether you run a small workshop, a manufacturing plant, a retail store, or a service agency, Lysarq helps you find new buyers, discover locations with high customer demand and zero competition, and see which markets are booming. No confusing jargon — just practical facts to help you win more orders and grow your profit.',
      verifiedBadge: 'Official Government Data',
      statRegistered: 'Registered Businesses in',
      statPincodes: 'Pin Code Areas Covered',
      statTopSector: 'Top Sector in Region',
      statNewShops: 'New Shops & Units Added',
      statMarketGaps: 'High-Demand Market Gaps',
      formTitle: 'Get Free Market Intelligence for Your Business',
      formSubtitle: 'Fast 1-minute inquiry. We will send you tailored MSME market density reports and customer growth opportunities.',
      nameLabel: 'Your Full Name',
      companyLabel: 'Business / Shop Name',
      emailLabel: 'Email Address',
      phoneLabel: 'Mobile Number (10 digits)',
      stateLabel: 'State / District',
      cityLabel: 'City / Town',
      turnoverLabel: 'Annual Sales / Turnover',
      requirementLabel: 'What would you like help with?',
      submitButton: 'Get Free Market Report',
      submitting: 'Submitting...',
      formSuccessTitle: 'Request Received Successfully!',
      formSuccessMsg: 'Our MSME growth specialists will analyze your district market data and reach out within 24 hours.',
      showcaseHeading: 'Interactive Opportunity Sandbox',
      showcaseSubheading: 'Discover real market gaps and growth opportunities calculated from verified business records.',
      tabRadar: 'Growth Radar',
      tabWhitespace: 'Market Gaps',
      tabFastest: 'Fast Expansion',
    },
    search: {
      searchTitle: 'Competitor Check',
      pageTitle: 'Find Local Businesses & Competitors',
      pageSubtitle: 'Check shops and businesses, see how crowded your market is, and find the best locations across',
      tabPincode: 'PIN Code',
      tabKeyword: 'Business Keyword',
      tabSector: 'Industry (Category)',
      tabArea: 'Search in Area',
      pincodeLabel: 'PIN Code',
      keywordLabel: 'Business Type',
      sectorLabel: 'Industry Category',
      searchButton: 'Search',
      searching: 'Searching...',
      totalBusinesses: 'Total Shops & Businesses',
      competitors: 'Nearby Competitors',
      competitionLevel: 'How Crowded It Is',
      saturation: 'Market Space',
      recommendation: 'Advice & Opportunity',
      businesses: 'Shops & Businesses',
      sector: 'Category / Line of Work',
      location: 'Location',
      registered: 'Registered On',
      viewDetails: 'View Full Details',
      viewModeCards: 'Cards',
      viewModeTable: 'Table',
      viewModeMap: 'Map',
      exportCSV: 'Export CSV',
      selectedDistrict: 'Selected District',
      specificNeighborhood: 'Specific Neighborhood in',
      allNeighborhoods: 'All neighborhoods',
      filters: 'Filters:',
      areaLocality: 'Area / Locality',
    },
    analytics: {
      eyebrow: 'LIVE REGIONAL BUSINESS OVERVIEW',
      heading: 'Market Analytics & Business Density',
      subheading: 'Explore verified businesses, top industries, and local pin code hotspots across',
      categoriesPill: 'Business Categories',
      pincodesPill: 'Pin Code Areas',
      topSharePill: 'Biggest Industry Share',
      scaleEyebrow: 'BUSINESS SIZES & CATEGORIES',
      scaleTitle: 'Business Sizes Across',
      microDesc: 'Micro (Up to ₹1 Cr Machinery / ₹5 Cr Sales)',
      smallDesc: 'Small (Up to ₹10 Cr Machinery / ₹50 Cr Sales)',
      mediumDesc: 'Medium (Up to ₹50 Cr Machinery / ₹250 Cr Sales)',
      heatmapTitle: 'Regional Business Density Heatmap',
      filterPlaceholder: 'Filter categories or codes...',
      // Deep Analytics Keys
      dimPincode: 'PIN Codes',
      dimPincodeDesc: 'Where businesses are located',
      dimSection: 'Main Industry',
      dimSectionDesc: 'Broad sectors (e.g. Manufacturing, Retail)',
      dimDivision: 'Business Category',
      dimDivisionDesc: 'Industry types (e.g. Food, Textiles, IT)',
      dimGroup: 'Subcategory',
      dimGroupDesc: 'Specialized business groups',
      dimClass: 'Trade Type',
      dimClassDesc: 'Specific trades & services',
      dimSubclass: 'Specific Business',
      dimSubclassDesc: 'Exact trade & product activities',
      nicSection: 'Main Industry',
      nicDivision: 'Business Category',
      nicGroup: 'Subcategory',
      nicClass: 'Trade Type',
      nicSubclass: 'Specific Business',
      microTitle: 'MICRO BUSINESSES',
      smallTitle: 'SMALL ENTERPRISES',
      mediumTitle: 'MEDIUM ENTERPRISES',
      microCaption: 'Eligible for collateral-free bank loans (CGTMSE) and priority lending schemes',
      smallCaption: 'Eligible for technology upgrade grants and low-interest expansion loans',
      mediumCaption: 'Major regional employers and large buyers that order from local suppliers',
      signalsEyebrow: 'MARKET OPPORTUNITIES & ALERTS',
      signalsTitle: 'Key Market Signals & Growth Opportunities',
      signalsDesc: 'Important trends showing where customer demand is surging, which areas lack competition, and where new business hubs are forming.',
      signalFilterAll: 'All Signals',
      signalFilterDemand: 'Booming Demand',
      signalFilterWhitespace: 'Zero-Competition Areas',
      signalFilterHubs: 'Local Business Hubs',
      thSignalType: 'Signal Type',
      thCategoryArea: 'Business Category & Area',
      thTotalBiz: 'Total Businesses',
      thGrowthSpeed: 'Growth Speed',
      thConfidence: 'Confidence',
      thOpportunity: 'Recommended Opportunity',
      thWhyMatters: 'Why This Matters',
      whyThisBtn: 'Why is this?',
      heatmapEyebrow: 'LOCAL AREA & INDUSTRY DENSITY',
      heatmapDesc: 'See where businesses are heavily concentrated (darker blue) and where there is plenty of room for new shops (lighter blue).',
      heatmapPinGuide: 'Showing business density by postal delivery area. Darker blue cards indicate high commercial saturation; lighter cards highlight room for growth.',
      heatmapNicGuide: 'Official National Industrial Classification (NIC) categories mapping registered trade activities and economic sectors.',
      emptyHeatmapTitle: 'No Matching Areas or Industries',
      emptyHeatmapDesc: 'No items match your search. Try typing a different PIN code or keyword.',
      hierarchyEyebrow: 'INDUSTRY DRILL-DOWN FINDER',
      hierarchyTitle: 'Browse Businesses by Industry Categories',
      hierarchyDesc: 'Click through broad industries to narrow down into exact trade types, showing how many registered businesses operate in each.',
      currentLevelLabel: 'Current Level:',
      allIndustriesRoot: 'All Industries',
      clickDrillDown: 'Click to see specific trades in this category',
      terminalLevel: 'Specific Business (Exact Trade)',
      shareOfCategory: 'of this category',
      radarEyebrow: 'BUSINESS OPPORTUNITIES DISCOVERY',
      radarTitle: '4 Big Market Opportunities for Your Business',
      q1Title: 'Fast-Growing Industries',
      q1Sub: 'High Customer Demand & New Businesses Opening',
      q2Title: 'Zero-Competition Areas (Supply Gaps)',
      q2Sub: 'High Demand with Very Few Rival Shops',
      q3Title: 'Open Business Locations',
      q3Sub: 'Busy Pin Codes with Very Few Rival Shops',
      q4Title: 'Booming Neighborhood Hubs',
      q4Sub: 'Active Areas Adding New Businesses Quickly',
      thisYear: 'this year',
      totalShops: 'total shops',
      growth: 'growth',
    },
    trends: {
      eyebrow: 'BUSINESS GROWTH TRACKER · YEAR-BY-YEAR RECORDS',
      heading: 'Market Trends & Business Growth',
      subheading: 'See how many new businesses open each year, which industries are booming, and which areas are growing fastest across',
      timePeriod: 'Time Period',
      selectedArea: 'Selected Area',
      fastSlowHeader: 'Fastest & Slowest Growing Sectors',
      fastSlowNote: 'See which types of businesses are expanding quickly, and which ones have slowed down.',
      annualTrends: 'Annual Registration Trends',
      keyShifts: 'Key Market Movements',
    },
    forecast: {
      forecastTitle: 'Market Future Outlook (2-Year Trend)',
      pincodeForecasts: 'PIN Code Growth Prediction',
      sectorForecasts: 'Category Growth Prediction',
      year: 'Year',
      predicted: 'Expected Number of Shops',
      confidence: 'Reliability of Forecast',
      high: 'High Accuracy (Strong Track Record)',
      medium: 'Medium Accuracy (Fair Track Record)',
      low: 'Early Estimate (Limited History)',
      recommendation: 'Business Advice & Timing',
      interpretation: 'Why This Is Happening',
      action: 'Recommended Move',
      enter: 'Good Time to Open',
      wait: 'Wait and Watch (Monitor Market)',
      avoid: 'Crowded Market (High Risk of Rivalry)',
      impact: 'Expected Market Impact',
    },
    profile: {
      title: 'Business Profile & Decision Cockpit',
      subtitle: 'Benchmark your business against registered competitors, get expansion recommendations, and track state opportunities.',
      operatingDistrict: 'Operating District',
      operatingPin: 'PIN Code',
      industryCategory: 'Industry / Business Category',
      competitorsTitle: 'Nearby Competitors',
      growthTitle: 'District YoY Growth',
      saturationTitle: 'Market Saturation',
      saveProfile: 'Save Business Context',
      resetDefaults: 'Reset',
      // Deep Profile Keys
      pageEyebrow: 'MY BUSINESS PROFILE & GROWTH DASHBOARD',
      activeReady: 'Active & Ready',
      healthScoreBadge: 'Health Score',
      businessSizeLabel: 'Business Size',
      locationLabel: 'Location',
      cockpitTitle: 'Your Business Setup',
      editDetailsBtn: 'Edit Business Details ✎',
      hideDetailsBtn: 'Hide Details',
      quickScaleLabel: 'Business Size',
      districtLabel: 'District',
      pincodeLabel: 'PIN Code',
      industryLabel: 'Industry / Business Category',
      pincodePlaceholder: '6-digit PIN',
      selectIndustryDefault: 'Select Industry Division...',
      businessNameLabel: 'Business Name',
      businessNamePlaceholder: 'Enter your business or brand name',
      ownerLabel: 'Owner / Contact Person',
      ownerPlaceholder: 'Proprietor or partner name',
      emailLabel: 'Contact Email',
      emailPlaceholder: 'name@company.com',
      turnoverLabel: 'Yearly Sales / Turnover',
      privacyNotice: 'Your business details stay on your own computer. We never share them publicly.',
      cancelBtn: 'Cancel',
      saveDetailsBtn: 'Save Details',
      healthCardTitle: 'Business Health & Strength Score',
      healthCardDesc: 'An overall rating of your business strength based on local competition, market growth, and government scheme access.',
      whyScoreBtn: 'Why this score? ⓘ',
      overallRatingChip: 'Overall Business Rating',
      scoreGradeTag: 'Health Tier',
      riskCaption: 'Based on official registry data for your trade',
      keyRecommendationTitle: 'Key Recommendation for Your Business:',
      factorMarketGrowth: 'Market Growth',
      factorSpaceRivals: 'Space From Rivals',
      factorSizeSupport: 'Business Size Support',
      factorLocalHubs: 'Local Market Hubs',
      detailsLink: 'Details >',
      healthTierStrong: 'Strong & Resilient',
      healthTierHealthy: 'Healthy Growth',
      healthTierAttention: 'Needs Attention',
      riskLow: 'Low Competition Risk',
      riskModerate: 'Moderate Competition',
      riskHigh: 'High Market Competition',
      benchmarkTitle: 'Compare With Nearby Businesses',
      benchmarkDesc: 'See how your area compares with district averages and national numbers.',
      whyNumbersBtn: 'Why these numbers? >',
      tableMetricCol: 'Performance Metric',
      tableYourBizCol: 'Your Area (PIN)',
      tableDistrictAvgCol: 'District Average',
      tableNationalAvgCol: 'National Average',
      tableAssessmentCol: 'Market Assessment',
      seeCompetitorsBtn: 'See Competitors in this Area →',
      seeForecastBtn: 'See 2-Year Growth Outlook →',
      schemesHeading: 'Government Schemes & Subsidies for You',
      schemesSubtitle: 'Collateral-free credit, capital margin subsidies, technology grants, and tender preferences curated from the Ministry of MSME, SIDBI, and State Governments.',
      schemesSearchPlaceholder: 'Search 26 schemes by keyword (e.g. solar, collateral, loan, food, textile, subsidy, patent, export)...',
      scaleToggleMatched: 'Matched to',
      scaleToggleAll: 'All Business Sizes',
      filterAll: 'All Schemes',
      filterCredit: 'Loans & Credit',
      filterSubsidy: 'Subsidies & Grants',
      filterQuality: 'Quality, Tech & ESG',
      filterMarket: 'Govt Tenders & Exports',
      filterSpecial: 'Artisans, Women & Green',
      emptySchemesTitle: 'No Schemes Matched Your Filters',
      emptySchemesDesc: 'Try searching with a broader keyword, selecting another category, or toggling to "All Business Sizes".',
      resetFiltersBtn: 'Reset All Filters & View All 26 Schemes',
      matchLabel: 'Match',
      eligibleLabel: 'Eligible:',
      checkEligibilityBtn: 'Check Eligibility & Details →',
      officialPortalLink: 'Official Government Portal ↗',
      modalFinancialSupport: 'Financial Support / Limit',
      modalNodalAgency: 'Nodal Ministry / Agency',
      modalTargetSizes: 'Target Business Sizes',
      modalAboutTitle: 'About This Scheme',
      modalBenefitsTitle: 'What You Get (Benefits & Subsidies)',
      modalEligibilityTitle: 'Who Is Eligible',
      modalDocumentsTitle: 'Key Documents Required (Checklist)',
      modalApplyBtn: 'Apply on Official Government Portal ↗',
      modalCloseBtn: 'Close',
      expansionTitle: 'Best Neighboring Areas to Expand Into',
      expansionSubtitle: 'Nearby PIN codes with active markets and fewer competing shops in your trade.',
      expansionBadge: 'Areas with Low Competition in',
      emptyExpansionTitle: 'No Low-Competition Areas Found Nearby',
      emptyExpansionDesc: 'Try selecting a different district or category to find expansion opportunities.',
      openSpaceLabel: 'Open Market Space',
      existingCompetitorsLabel: 'Existing Competitors',
      totalShopsLabel: 'Total Shops in Area',
      explorePinBtn: 'Explore This PIN Code →',
      playbookTitle: 'Government Benefits & Legal Protections',
      playbookSubtitle: 'Legal rules to recover pending payments within 45 days, get bank loan interest discounts, and sell directly to government departments.',
      playbookBadge: 'Official Rights & Schemes',
      alertsTitle: 'Market Alerts & Notifications',
      alertsActive: 'Alerts Active',
      alertsSubtitle: 'Set alerts to get notified when new competitors open nearby or market conditions change.',
      savedSearchesTab: 'Saved Searches & Areas',
      watchedBizTab: 'Watched Businesses',
      bookmarkCurrentAreaBtn: 'Bookmark Current Area',
      viewModeTitle: 'Screen View Mode',
      comfortableMode: 'Spacious (Default)',
      compactMode: 'Compact (Show More Data)',
      resetAllSettingsBtn: 'Reset All Settings',
      saveSettingsBtn: 'Save Settings',
      savingBtn: 'Saving...',
    },
  },

  ta: {
    common: {
      micro: 'மைக்ரோ',
      small: 'சிறியது',
      medium: 'நடுத்தரம்',
      enterprises: 'நிறுவனங்கள்',
      businesses: 'வணிகங்கள்',
      shops: 'கடைகள்',
      all: 'அனைத்தும்',
      search: 'தேடு',
      close: 'மூடு',
      save: 'சேமி',
      cancel: 'ரத்து',
      reset: 'மீட்டமை',
      loading: 'ஏற்றுகிறது...',
      active: 'செயலில்',
      details: 'விவரங்கள்',
      explore: 'ஆராய்க',
      verified: 'சரிபார்க்கப்பட்டது',
      status: 'நிலை',
    },
    nav: {
      home: 'மேலோட்டம்',
      analytics: 'பகுப்பாய்வு',
      search: 'ஆராய்க',
      trends: 'போக்குகள்',
      forecast: 'முன்னோக்கு',
      profile: 'எனது வணிகம்',
      appTitle: 'வணிக நுண்ணறிவு',
      brandName: 'LYSARQ',
      brandSub: 'MSME',
      searchPlaceholder: 'கடைகள், அஞ்சல் குறியீடுகள், தொழில்களைத் தேடுங்கள்...',
      saveView: 'காட்சியைச் சேமி',
      download: 'பதிவிறக்கு',
      share: 'பகிர்',
      updatingMarket: 'சந்தை தரவு புதுப்பிக்கப்படுகிறது',
      platformVisitors: 'தள பார்வையாளர்கள்',
      languageLabel: 'Language / மொழி / ഭാഷ / भाषा:',
      tools: 'கருவிகள்',
      allTools: 'அனைத்து கருவிகள்',
      contactUs: 'எங்களை தொடர்பு கொள்ள',
      needAi: 'உங்கள் வணிகத்திற்கு AI தேவையா?',
    },
    visitor: {
      label: 'மொத்த பார்வையாளர்கள்',
      short: 'பார்வையாளர்கள்',
      today: 'இன்றைய பார்வையாளர்கள்',
      allTime: 'மொத்த வருகை',
      status: 'சரிபார்க்கப்பட்ட எண்ணிக்கை',
      footer: 'அமர்வு சரிபார்க்கப்பட்ட தகவல்',
    },
    dashboard: {
      heroHeading1: 'புதிய வாடிக்கையாளர்களைக் கண்டறிந்து விற்பனையை அதிகரிக்கவும்',
      heroHeading2: 'எளிய, சரிபார்க்கப்பட்ட சந்தைத் தரவுகளுடன்',
      heroSubheading: 'உண்மையான வாங்குபவர்களைக் கண்டறியவும், அருகிலுள்ள போட்டியாளர்களைச் சரிபார்க்கவும், தேவை எங்கு வளர்கிறது என்பதைப் பார்க்கவும்',
      heroNarrative: 'இந்தியா முழுவதும் உண்மையான, சரிபார்க்கப்பட்ட வணிகத் தரவுகளுடன் உங்கள் விற்பனையை வளர்த்துக் கொள்ளுங்கள். நீங்கள் ஒரு சிறிய பட்டறை, உற்பத்தி ஆலை, சில்லறை கடை அல்லது சேவை நிறுவனத்தை நடத்தினாலும், லைசார்க் புதிய வாங்குபவர்களைக் கண்டறியவும், போட்டியில்லாத உயர் தேவை இடங்களைக் கண்டறியவும், எந்த சந்தைகள் வேகமாக வளர்கின்றன என்பதை அறியவும் உதவுகிறது.',
      verifiedBadge: 'அதிகாரப்பூர்வ அரசு தரவு',
      statRegistered: 'பதிவுசெய்யப்பட்ட வணிகங்கள்',
      statPincodes: 'உள்ளடக்கப்பட்ட அஞ்சல் குறியீடுகள்',
      statTopSector: 'பிராந்திய முன்னணித் துறை',
      statNewShops: 'புதிய கடைகள் & அலகுகள்',
      statMarketGaps: 'அதிக தேவை சந்தை இடைவெளிகள்',
      formTitle: 'உங்கள் வணிகத்திற்கான இலவச சந்தை நுண்ணறிவைப் பெறுங்கள்',
      formSubtitle: 'விரைவான 1 நிமிட விசாரணை. நாங்கள் உங்களுக்கு விரிவான சந்தை அடர்த்தி அறிக்கை மற்றும் வாடிக்கையாளர் வளர்ச்சி வாய்ப்புகளை அனுப்புவோம்.',
      nameLabel: 'உங்கள் முழு பெயர்',
      companyLabel: 'வணிகம் / கடையின் பெயர்',
      emailLabel: 'மின்னஞ்சல் முகவரி',
      phoneLabel: 'கைபேசி எண் (10 இலக்கங்கள்)',
      stateLabel: 'மாநிலம் / மாவட்டம்',
      cityLabel: 'நகரம் / ஊர்',
      turnoverLabel: 'ஆண்டு விற்பனை / விற்றுமுதல்',
      requirementLabel: 'உங்களுக்கு என்ன உதவி தேவை?',
      submitButton: 'இலவச சந்தை அறிக்கையைப் பெறுங்கள்',
      submitting: 'சமர்ப்பிக்கப்படுகிறது...',
      formSuccessTitle: 'நன்றி! கோரிக்கை பெறப்பட்டது',
      formSuccessMsg: 'எங்கள் MSME வளர்ச்சி நிபுணர்கள் உங்கள் மாவட்ட சந்தைத் தரவை பகுப்பாய்வு செய்து 24 மணி நேரத்திற்குள் உங்களைத் தொடர்புகொள்வார்கள்.',
      showcaseHeading: 'ஊடாடும் வாய்ப்பு மையம்',
      showcaseSubheading: 'சரிபார்க்கப்பட்ட வணிகப் பதிவுகளிலிருந்து கணக்கிடப்பட்ட உண்மையான சந்தை இடைவெளிகள் மற்றும் வளர்ச்சி வாய்ப்புகளைக் கண்டறியவும்.',
      tabRadar: 'வளர்ச்சி ரேடார்',
      tabWhitespace: 'சந்தை இடைவெளிகள்',
      tabFastest: 'வேகமான விரிவாக்கம்',
    },
    search: {
      searchTitle: 'போட்டியாளர் பகுப்பாய்வு',
      pageTitle: 'உள்ளூர் வணிகங்கள் மற்றும் போட்டியாளர்களைக் கண்டறியவும்',
      pageSubtitle: 'கடைகள் மற்றும் வணிகங்களைச் சரிபார்க்கவும், உங்கள் சந்தை எவ்வளவு நிறைந்துள்ளது என்பதைப் பார்க்கவும், சிறந்த இடங்களைக் கண்டறியவும்:',
      tabPincode: 'அஞ்சல் குறியீடு',
      tabKeyword: 'வணிக முக்கியச்சொல்',
      tabSector: 'தொழில் துறை (பிரிவு)',
      tabArea: 'பகுதியில் தேடு',
      pincodeLabel: 'அஞ்சல் குறியீடு',
      keywordLabel: 'வணிக வகை',
      sectorLabel: 'தொழில் துறை',
      searchButton: 'தேடு',
      searching: 'தேடுகிறது...',
      totalBusinesses: 'மொத்த வணிகங்கள்',
      competitors: 'பொருந்தும் போட்டியாளர்கள்',
      competitionLevel: 'போட்டி நிலை',
      saturation: 'சந்தை நிறைவு',
      recommendation: 'பரிந்துரை மற்றும் வாய்ப்பு',
      businesses: 'வணிகங்கள்',
      sector: 'துறை / பணி வகை',
      location: 'இடம்',
      registered: 'பதிவு செய்யப்பட்டது',
      viewDetails: 'முழு விவரங்களைக் காண்க',
      viewModeCards: 'கார்டுகள்',
      viewModeTable: 'அட்டவணை',
      viewModeMap: 'வரைபடம்',
      exportCSV: 'CSV பதிவிறக்கு',
      selectedDistrict: 'தேர்ந்தெடுக்கப்பட்ட மாவட்டம்',
      specificNeighborhood: 'குறிப்பிட்ட பகுதி',
      allNeighborhoods: 'அனைத்து பகுதிகளும்',
      filters: 'வடிகட்டிகள்:',
      areaLocality: 'பகுதி / வட்டாரம்',
    },
    analytics: {
      eyebrow: 'நேரலை பிராந்திய வணிக மேலோட்டம்',
      heading: 'சந்தை பகுப்பாய்வு மற்றும் வணிக அடர்த்தி',
      subheading: 'சரிபார்க்கப்பட்ட வணிகங்கள், முன்னணித் தொழில்கள் மற்றும் உள்ளூர் அஞ்சல் குறியீட்டு மையங்களை ஆராயுங்கள்:',
      categoriesPill: 'வணிகப் பிரிவுகள்',
      pincodesPill: 'அஞ்சல் குறியீட்டு பகுதிகள்',
      topSharePill: 'முன்னணித் தொழில் பங்கு',
      scaleEyebrow: 'வணிக அளவுகள் மற்றும் பிரிவுகள்',
      scaleTitle: 'வணிக அளவுகளின் பகிர்வு',
      microDesc: 'மைக்ரோ (₹1 கோடி வரை இயந்திரங்கள் / ₹5 கோடி விற்பனை)',
      smallDesc: 'சிறியது (₹10 கோடி வரை இயந்திரங்கள் / ₹50 கோடி விற்பனை)',
      mediumDesc: 'நடுத்தரம் (₹50 கோடி வரை இயந்திரங்கள் / ₹250 கோடி விற்பனை)',
      heatmapTitle: 'பிராந்திய வணிக அடர்த்தி வரைபடம்',
      filterPlaceholder: 'வகைகளை வடிகட்டவும்...',
      // Deep Analytics Keys
      dimPincode: 'அஞ்சல் குறியீடுகள்',
      dimPincodeDesc: 'வணிகங்கள் அமைந்துள்ள இடங்கள்',
      dimSection: 'முதன்மைத் தொழில்',
      dimSectionDesc: 'பரந்த பிரிவுகள் (உற்பத்தி, சில்லறை போன்றவை)',
      dimDivision: 'வணிகப் பிரிவு',
      dimDivisionDesc: 'தொழில் வகைகள் (உணவு, ஜவுளி, தகவல் தொழில்நுட்பம்)',
      dimGroup: 'துணைப் பிரிவு',
      dimGroupDesc: 'குறிப்பிட்ட வணிகக் குழுக்கள்',
      dimClass: 'வர்த்தக வகை',
      dimClassDesc: 'குறிப்பிட்ட வர்த்தகங்கள் & சேவைகள்',
      dimSubclass: 'குறிப்பிட்ட வணிகம்',
      dimSubclassDesc: 'துல்லியமான வர்த்தகம் மற்றும் தயாரிப்பு செயல்பாடுகள்',
      nicSection: 'முதன்மைத் தொழில்',
      nicDivision: 'வணிகப் பிரிவு',
      nicGroup: 'துணைப் பிரிவு',
      nicClass: 'வர்த்தக வகை',
      nicSubclass: 'குறிப்பிட்ட வணிகம்',
      microTitle: 'மைக்ரோ வணிகங்கள்',
      smallTitle: 'சிறிய நிறுவனங்கள்',
      mediumTitle: 'நடுத்தர நிறுவனங்கள்',
      microCaption: 'பிணையற்ற வங்கி கடன்கள் (CGTMSE) மற்றும் முன்னுரிமை கடன்களுக்கு தகுதியானது',
      smallCaption: 'தொழில்நுட்ப மேம்பாட்டு மானியங்கள் மற்றும் குறைந்த வட்டி கடன்களுக்கு தகுதியானது',
      mediumCaption: 'உள்ளூர் சப்ளையர்களிடமிருந்து வாங்கும் பெரிய நிறுவனங்கள்',
      signalsEyebrow: 'சந்தை வாய்ப்புகள் & எச்சரிக்கைகள்',
      signalsTitle: 'முக்கிய சந்தை சமிக்ஞைகள் & வளர்ச்சி வாய்ப்புகள்',
      signalsDesc: 'வாடிக்கையாளர் தேவை எங்கு உயர்கிறது, போட்டியில்லாத பகுதிகள் மற்றும் புதிய வணிக மையங்களை வெளிப்படுத்தும் போக்குகள்.',
      signalFilterAll: 'அனைத்து சமிக்ஞைகள்',
      signalFilterDemand: 'உயரும் தேவை',
      signalFilterWhitespace: 'போட்டியில்லா பகுதிகள்',
      signalFilterHubs: 'உள்ளூர் வணிக மையங்கள்',
      thSignalType: 'சமிக்ஞை வகை',
      thCategoryArea: 'வணிகப் பிரிவு & பகுதி',
      thTotalBiz: 'மொத்த வணிகங்கள்',
      thGrowthSpeed: 'வளர்ச்சி வேகம்',
      thConfidence: 'நம்பகத்தன்மை',
      thOpportunity: 'பரிந்துரைக்கப்பட்ட வாய்ப்பு',
      thWhyMatters: 'இது ஏன் முக்கியம்',
      whyThisBtn: 'விளக்கம் என்ன?',
      heatmapEyebrow: 'உள்ளூர் பகுதி & தொழில் அடர்த்தி',
      heatmapDesc: 'வணிகங்கள் எங்கு அதிகமாக உள்ளன (அடர் நீலம்) மற்றும் எங்கு அதிக வாய்ப்பு உள்ளது (இள நீலம்) என்பதைப் பார்க்கவும்.',
      heatmapPinGuide: 'அஞ்சல் குறியீட்டு பகுதி வாரியாக வணிக அடர்த்தி. அடர் நீலம் அதிக போட்டியை குறிக்கிறது; இள நீலம் வளர்ச்சி வாய்ப்புகளை காட்டுகிறது.',
      heatmapNicGuide: 'தேசிய தொழில்துறை வகைப்பாடு (NIC) வர்த்தக செயல்பாடுகள் மற்றும் பொருளாதார துறைகள்.',
      emptyHeatmapTitle: 'பொருந்தும் பகுதிகள் அல்லது தொழில்கள் இல்லை',
      emptyHeatmapDesc: 'உங்கள் தேடலுடன் எதுவும் பொருந்தவில்லை. வேறு அஞ்சல் குறியீடு அல்லது முக்கிய வார்த்தையை முயற்சிக்கவும்.',
      hierarchyEyebrow: 'தொழில் வாரியான தேடல்',
      hierarchyTitle: 'தொழில் பிரிவுகள் வாரியாக வணிகங்களை ஆராயுங்கள்',
      hierarchyDesc: 'துல்லியமான வர்த்தக வகைகளைக் கண்டறிய முதன்மைத் துறைகள் வழியாக கிளிக் செய்து செல்லுங்கள்.',
      currentLevelLabel: 'தற்போதைய நிலை:',
      allIndustriesRoot: 'அனைத்து தொழில்கள்',
      clickDrillDown: 'இந்தப் பிரிவில் உள்ள குறிப்பிட்ட வர்த்தகங்களைக் காண கிளிக் செய்க',
      terminalLevel: 'குறிப்பிட்ட வணிகம் (துல்லியமான வர்த்தகம்)',
      shareOfCategory: 'இந்தப் பிரிவின் பங்கு',
      radarEyebrow: 'வணிக வாய்ப்புகள் கண்டுபிடிப்பு',
      radarTitle: 'உங்கள் வணிகத்திற்கான 4 பெரிய சந்தை வாய்ப்புகள்',
      q1Title: 'வேகமாக வளரும் தொழில்கள்',
      q1Sub: 'உயர் வாடிக்கையாளர் தேவை & புதிய வணிகங்கள் தொடக்கம்',
      q2Title: 'போட்டியில்லா பகுதிகள் (விநியோக இடைவெளி)',
      q2Sub: 'அதிக தேவையுடன் மிகக் குறைந்த போட்டியாளர்கள்',
      q3Title: 'திறந்த வணிக இடங்கள்',
      q3Sub: 'குறைந்த போட்டியாளர்களுடன் சுறுசுறுப்பான அஞ்சல் குறியீடுகள்',
      q4Title: 'வளர்ந்து வரும் வணிக மையங்கள்',
      q4Sub: 'புதிய வணிகங்களை வேகமாக ஈர்க்கும் பகுதிகள்',
      thisYear: 'இந்த ஆண்டு',
      totalShops: 'மொத்த கடைகள்',
      growth: 'வளர்ச்சி',
    },
    trends: {
      eyebrow: 'வணிக வளர்ச்சி கண்காணிப்பு · ஆண்டு வாரியான பதிவுகள்',
      heading: 'சந்தை போக்குகள் மற்றும் வணிக வளர்ச்சி',
      subheading: 'ஒவ்வொரு ஆண்டும் எத்தனை புதிய வணிகங்கள் தொடங்கப்படுகின்றன, எந்தத் தொழில்கள் வேகமாக வளர்கின்றன என்பதைப் பார்க்கவும்:',
      timePeriod: 'கால அளவு',
      selectedArea: 'தேர்ந்தெடுக்கப்பட்ட பகுதி',
      fastSlowHeader: 'வேகமாக மற்றும் மெதுவாக வளரும் துறைகள்',
      fastSlowNote: 'எந்த வகையான வணிகங்கள் வேகமாக விரிவடைகின்றன, எவை குறைந்துள்ளன என்பதைப் பார்க்கவும்.',
      annualTrends: 'ஆண்டு பதிவு போக்குகள்',
      keyShifts: 'முக்கிய சந்தை மாற்றங்கள்',
    },
    forecast: {
      forecastTitle: 'சந்தார முன்னறிவிப்பு மற்றும் பாதை (2 ஆண்டுகள்)',
      pincodeForecasts: 'அஞ்சல் குறியீட்டு வளர்ச்சி முன்னறிவிப்பு',
      sectorForecasts: 'தொழில் துறை போட்டி முன்னறிவிப்பு',
      year: 'ஆண்டு',
      predicted: 'முன்னறிவிப்பு எண்ணிக்கை',
      confidence: 'நம்பிக்கை',
      high: 'அதிக நம்பிக்கை (வலுவான வரலாறு)',
      medium: 'நடுத்தர நம்பிக்கை (சராசரி வரலாறு)',
      low: 'குறைந்த நம்பிக்கை (ஆரம்ப மதிப்பீடு)',
      recommendation: 'மூலோபாய நுழைவு பரிந்துரை',
      interpretation: 'மேக்ரோ இயக்கவியல் விளக்கம்',
      action: 'பரிந்துரைக்கப்பட்ட நடவடிக்கை',
      enter: 'சாதகமான நுழைவு காலம்',
      wait: 'காத்திருந்து கண்காணிக்கவும்',
      avoid: 'அதிக நிறைவு ஆபத்து - எச்சரிக்கை',
      impact: 'சந்தை தாக்கம்',
    },
    profile: {
      title: 'வணிக சுயவிவரம் மற்றும் முடிவெடுக்கும் மையம்',
      subtitle: 'உங்கள் வணிகத்தை பதிவுசெய்யப்பட்ட போட்டியாளர்களுடன் ஒப்பிட்டு, விரிவாக்க வாய்ப்புகளைக் கண்டறியவும்.',
      operatingDistrict: 'செயல்படும் மாவட்டம்',
      operatingPin: 'அஞ்சல் குறியீடு',
      industryCategory: 'தொழில் / வணிகப் பிரிவு',
      competitorsTitle: 'அருகிலுள்ள போட்டியாளர்கள்',
      growthTitle: 'மாவட்ட ஆண்டு வளர்ச்சி',
      saturationTitle: 'சந்தை நிறைவு நிலை',
      saveProfile: 'வணிக விவரங்களைச் சேமி',
      resetDefaults: 'மீட்டமை',
      // Deep Profile Keys
      pageEyebrow: 'எனது வணிக சுயவிவரம் & வளர்ச்சி டேஷ்போர்டு',
      activeReady: 'செயலில் உள்ளது & தயார்',
      healthScoreBadge: 'ஆரோக்கிய மதிப்பீடு',
      businessSizeLabel: 'வணிக அளவு',
      locationLabel: 'இருப்பிடம்',
      cockpitTitle: 'உங்கள் வணிக அமைப்பு',
      editDetailsBtn: 'வணிக விவரங்களைத் திருத்துக ✎',
      hideDetailsBtn: 'விவரங்களை மறை',
      quickScaleLabel: 'வணிக அளவு',
      districtLabel: 'மாவட்டம்',
      pincodeLabel: 'அஞ்சல் குறியீடு',
      industryLabel: 'தொழில் / வணிகப் பிரிவு',
      pincodePlaceholder: '6-இலக்க அஞ்சல் குறியீடு',
      selectIndustryDefault: 'தொழில் பிரிவைத் தேர்ந்தெடுக்கவும்...',
      businessNameLabel: 'வணிகத்தின் பெயர்',
      businessNamePlaceholder: 'உங்கள் கடை அல்லது பிராண்ட் பெயரை உள்ளிடவும்',
      ownerLabel: 'உரிமையாளர் / தொடர்பாளர் பெயர்',
      ownerPlaceholder: 'உரிமையாளர் பெயர்',
      emailLabel: 'மின்னஞ்சல் முகவரி',
      emailPlaceholder: 'name@company.com',
      turnoverLabel: 'ஆண்டு விற்பனை / விற்றுமுதல்',
      privacyNotice: 'உங்கள் வணிக விவரங்கள் உங்கள் சாதனத்தில் பாதுகாப்பாக இருக்கும். நாங்கள் பகிர மாட்டோம்.',
      cancelBtn: 'ரத்து',
      saveDetailsBtn: 'விவரங்களைச் சேமி',
      healthCardTitle: 'வணிக ஆரோக்கியம் மற்றும் வலிமை மதிப்பீடு',
      healthCardDesc: 'உள்ளூர் போட்டி, சந்தை வளர்ச்சி மற்றும் அரசு திட்ட அணுகல் ஆகியவற்றின் அடிப்படையிலான ஒட்டுமொத்த மதிப்பீடு.',
      whyScoreBtn: 'இந்த மதிப்பீடு ஏன்? ⓘ',
      overallRatingChip: 'ஒட்டுமொத்த வணிக மதிப்பீடு',
      scoreGradeTag: 'ஆரோக்கிய நிலை',
      riskCaption: 'உங்கள் வர்த்தகத்திற்கான அதிகாரப்பூர்வ அரசு பதிவுகளின்படி',
      keyRecommendationTitle: 'உங்கள் வணிகத்திற்கான முக்கிய பரிந்துரை:',
      factorMarketGrowth: 'சந்தை வளர்ச்சி',
      factorSpaceRivals: 'போட்டியாளர்களிடமிருந்து இடைவெளி',
      factorSizeSupport: 'வணிக அளவு முன்னுரிமை',
      factorLocalHubs: 'உள்ளூர் வர்த்தக மையங்கள்',
      detailsLink: 'விவரங்கள் >',
      healthTierStrong: 'வலுவான & மீள்திறன் கொண்டது',
      healthTierHealthy: 'ஆரோக்கியமான வளர்ச்சி',
      healthTierAttention: 'கவனம் தேவை',
      riskLow: 'குறைந்த போட்டி ஆபத்து',
      riskModerate: 'மிதமான போட்டி',
      riskHigh: 'அதிக சந்தை போட்டி',
      benchmarkTitle: 'அருகிலுள்ள வணிகங்களுடன் ஒப்பிடுங்கள்',
      benchmarkDesc: 'உங்கள் பகுதி மாவட்ட சராசரி மற்றும் தேசிய எண்களுடன் எவ்வாறு ஒப்பிடப்படுகிறது என்பதைப் பார்க்கவும்.',
      whyNumbersBtn: 'இந்த எண்கள் ஏன்? >',
      tableMetricCol: 'செயல்திறன் அளவீடு',
      tableYourBizCol: 'உங்கள் பகுதி (PIN)',
      tableDistrictAvgCol: 'மாவட்ட சராசரி',
      tableNationalAvgCol: 'தேசிய சராசரி',
      tableAssessmentCol: 'சந்தை மதிப்பீடு',
      seeCompetitorsBtn: 'இப்பகுதியில் போட்டியாளர்களைக் காண்க →',
      seeForecastBtn: '2-ஆண்டு வளர்ச்சி முன்னறிவிப்பைக் காண்க →',
      schemesHeading: 'உங்களுக்கான அரசு திட்டங்கள் & மானியங்கள்',
      schemesSubtitle: 'பிணையற்ற கடன், மூலதன மானியம், தொழில்நுட்ப உதவிகள் மற்றும் அரசு கொள்முதல் முன்னுரிமைகள்.',
      schemesSearchPlaceholder: 'முக்கிய வார்த்தை மூலம் 26 திட்டங்களைத் தேடுங்கள் (எ.கா. கடன், மானியம், சோலார், ஜவுளி, உணவு)...',
      scaleToggleMatched: 'பொருந்திய திட்டங்கள்',
      scaleToggleAll: 'அனைத்து வணிக அளவுகளும்',
      filterAll: 'அனைத்து திட்டங்கள்',
      filterCredit: 'கடன்கள் & கடன் உத்தரவாதம்',
      filterSubsidy: 'மூலதன மானியங்கள்',
      filterQuality: 'தரம், தொழில்நுட்பம் & ESG',
      filterMarket: 'அரசு டெண்டர்கள் & ஏற்றுமதி',
      filterSpecial: 'கைவினைஞர்கள், பெண்கள் & பசுமை',
      emptySchemesTitle: 'பொருந்தும் திட்டங்கள் எதுவும் இல்லை',
      emptySchemesDesc: 'வேறு வார்த்தையைத் தேடவும் அல்லது "அனைத்து வணிக அளவுகளும்" என்பதைத் தேர்ந்தெடுக்கவும்.',
      resetFiltersBtn: 'வடிகட்டிகளை மீட்டமைத்து 26 திட்டங்களையும் காண்க',
      matchLabel: 'பொருத்தம்',
      eligibleLabel: 'தகுதி:',
      checkEligibilityBtn: 'தகுதி & விவரங்களைச் சரிபார்க்கவும் →',
      officialPortalLink: 'அதிகாரப்பூர்வ அரசு தளம் ↗',
      modalFinancialSupport: 'நிதி ஆதரவு / உச்சவரம்பு',
      modalNodalAgency: 'பொறுப்பு அமைச்சகம் / நிறுவனம்',
      modalTargetSizes: 'தகுதியான வணிக அளவுகள்',
      modalAboutTitle: 'இத்திட்டம் பற்றி',
      modalBenefitsTitle: 'நீங்கள் பெறும் நன்மைகள் & மானியங்கள்',
      modalEligibilityTitle: 'யார் தகுதியானவர்கள்',
      modalDocumentsTitle: 'தேவையான முக்கிய ஆவணங்கள் (சரிபார்ப்புப் பட்டியல்)',
      modalApplyBtn: 'அதிகாரப்பூர்வ அரசு போர்ட்டலில் விண்ணப்பிக்கவும் ↗',
      modalCloseBtn: 'மூடு',
      expansionTitle: 'விரிவாக்கம் செய்ய சிறந்த அண்டை பகுதிகள்',
      expansionSubtitle: 'செயலில் உள்ள சந்தைகள் மற்றும் குறைந்த போட்டியாளர்களைக் கொண்ட அருகிலுள்ள அஞ்சல் குறியீடுகள்.',
      expansionBadge: 'குறைந்த போட்டியுள்ள பகுதிகள்:',
      emptyExpansionTitle: 'அருகில் குறைந்த போட்டியுள்ள பகுதிகள் கிடைக்கவில்லை',
      emptyExpansionDesc: 'விரிவாக்க வாய்ப்புகளைக் கண்டறிய வேறு மாவட்டம் அல்லது பிரிவைத் தேர்ந்தெடுக்கவும்.',
      openSpaceLabel: 'திறந்த சந்தை இடம்',
      existingCompetitorsLabel: 'தற்போதுள்ள போட்டியாளர்கள்',
      totalShopsLabel: 'பகுதியில் உள்ள மொத்த கடைகள்',
      explorePinBtn: 'இந்த அஞ்சல் குறியீட்டை ஆராய்க →',
      playbookTitle: 'அரசு நன்மைகள் & சட்டப் பாதுகாப்புகள்',
      playbookSubtitle: 'நிலுவைத் தொகையை 45 நாட்களுக்குள் வசூலிக்கும் சட்ட விதிகள், வட்டி தள்ளுபடி மற்றும் அரசு விற்பனை நன்மைகள்.',
      playbookBadge: 'அதிகாரப்பூர்வ உரிமைகள் & திட்டங்கள்',
      alertsTitle: 'சந்தை எச்சரிக்கைகள் & அறிவிப்புகள்',
      alertsActive: 'எச்சரிக்கைகள் செயலில் உள்ளன',
      alertsSubtitle: 'அருகில் புதிய போட்டியாளர்கள் வரும்போது அல்லது சந்தை மாறும் போது அறிவிப்பைப் பெறுங்கள்.',
      savedSearchesTab: 'சேமிக்கப்பட்ட தேடல்கள் & பகுதிகள்',
      watchedBizTab: 'கண்காணிக்கப்படும் வணிகங்கள்',
      bookmarkCurrentAreaBtn: 'தற்போதைய பகுதியைச் சேமி',
      viewModeTitle: 'திரை காட்சி முறை',
      comfortableMode: 'இடைவெளியுடன் (வழக்கமானது)',
      compactMode: 'சுருக்கமானது (அதிக தரவு)',
      resetAllSettingsBtn: 'அனைத்து அமைப்புகளையும் மீட்டமை',
      saveSettingsBtn: 'அமைப்புகளைச் சேமி',
      savingBtn: 'சேமிக்கப்படுகிறது...',
    },
  },

  ml: {
    common: {
      micro: 'മൈക്രോ',
      small: 'സ്മോൾ',
      medium: 'മീഡിയം',
      enterprises: 'സംരംഭങ്ങൾ',
      businesses: 'ബിസിനസുകൾ',
      shops: 'കടകൾ',
      all: 'എല്ലാം',
      search: 'തിരയുക',
      close: 'അടയ്ക്കുക',
      save: 'സംരക്ഷിക്കുക',
      cancel: 'റദ്ദാക്കുക',
      reset: 'പുനഃക്രമീകരിക്കുക',
      loading: 'ലോഡ് ചെയ്യുന്നു...',
      active: 'സജീവം',
      details: 'വിശദാംശങ്ങൾ',
      explore: 'പര്യവേക്ഷണം',
      verified: 'സ്ഥിരീകരിച്ചത്',
      status: 'നില',
    },
    nav: {
      home: 'അവലോകനം',
      analytics: 'വിശകലനം',
      search: 'പര്യവേക്ഷണം',
      trends: 'ട്രെൻഡുകൾ',
      forecast: 'ഔട്ട്‌ലുക്ക്',
      profile: 'എന്റെ ബിസിനസ്',
      appTitle: 'ബിസിനസ്സ് ഇന്റലിജൻസ്',
      brandName: 'LYSARQ',
      brandSub: 'MSME',
      searchPlaceholder: 'കടകൾ, പിൻകോഡുകൾ, ബിസിനസുകൾ തിരയുക...',
      saveView: 'കാഴ്ച സംരക്ഷിക്കുക',
      download: 'ഡൗൺലോഡ്',
      share: 'പങ്കിടുക',
      updatingMarket: 'മാർക്കറ്റ് ഡാറ്റ അപ്ഡേറ്റ് ചെയ്യുന്നു',
      platformVisitors: 'പ്ലാറ്റ്ഫോം സന്ദർശകർ',
      languageLabel: 'Language / மொழி / ഭാഷ / भाषा:',
      tools: 'ടൂളുകൾ',
      allTools: 'എല്ലാ ഉപകരണങ്ങളും',
      contactUs: 'ഞങ്ങളെ ബന്ധപ്പെടുക',
      needAi: 'നിങ്ങളുടെ ബിസിനസ്സിന് AI ആവശ്യമുണ്ടോ?',
    },
    visitor: {
      label: 'ആകെ സന്ദർശകർ',
      short: 'സന്ദർശകർ',
      today: 'ഇന്നത്തെ സന്ദർശകർ',
      allTime: 'ആകെ സന്ദർശനങ്ങൾ',
      status: 'സ്ഥിരീകരിച്ച കൗണ്ടർ',
      footer: 'സെഷൻ സ്ഥിരീകരിച്ച വിശകലനം',
    },
    dashboard: {
      heroHeading1: 'പുതിയ ഉപഭോക്താക്കളെ കണ്ടെത്തുകയും വിൽപ്പന വർദ്ധിപ്പിക്കുകയും ചെയ്യുക',
      heroHeading2: 'ലളിതവും സ്ഥിരീകരിച്ചതുമായ മാർക്കറ്റ് ഡാറ്റയോടെ',
      heroSubheading: 'യഥാർത്ഥ വാങ്ങലുകാരെ കണ്ടെത്തുക, അടുത്തുള്ള എതിരാളികളെ പരിശോധിക്കുക, ഡിമാൻഡ് എവിടെയാണ് വളരുന്നതെന്ന് കാണുക',
      heroNarrative: 'ഇന്ത്യയിലുടനീളമുള്ള യഥാർത്ഥ, സ്ഥിരീകരിച്ച ബിസിനസ്സ് ഡാറ്റ ഉപയോഗിച്ച് നിങ്ങളുടെ വിൽപ്പന വർദ്ധിപ്പിക്കുക. നിങ്ങൾ ഒരു ചെറിയ വർക്ക്‌ഷോപ്പോ നിർമ്മാണശാലയോ റീട്ടെയിൽ കടയോ സേവന സ്ഥാപനമോ നടത്തുന്ന ആളായാലും, പുതിയ ഉപഭോക്താക്കളെ കണ്ടെത്താനും ഉയർന്ന ഡിമാൻഡുള്ള മത്സരമില്ലാത്ത വിപണികൾ കണ്ടെത്താനും ലൈസാർക്ക് നിങ്ങളെ സഹായിക്കുന്നു.',
      verifiedBadge: 'ഔദ്യോഗിക സർക്കാർ ഡാറ്റ',
      statRegistered: 'രജിസ്റ്റർ ചെയ്ത ബിസിനസുകൾ',
      statPincodes: 'ഉൾപ്പെടുത്തിയ പിൻകോഡുകൾ',
      statTopSector: 'മേഖലയിലെ മുൻനിര വ്യവസായം',
      statNewShops: 'പുതിയ കടകളും യൂണിറ്റുകളും',
      statMarketGaps: 'ഉയർന്ന ഡിമാൻഡ് വിപണി വിടവുകൾ',
      formTitle: 'നിങ്ങളുടെ ബിസിനസ്സിനായുള്ള സൗജന്യ മാർക്കറ്റ് ഇന്റലിജൻസ് നേടുക',
      formSubtitle: '1 മിനിറ്റ് അന്വേഷണം. നിങ്ങളുടെ പ്രദേശത്തെ വിപണി സാന്ദ്രതാ റിപ്പോർട്ടും ഉപഭോക്തൃ വളർച്ചാ അവസരങ്ങളും ഞങ്ങൾ അയച്ചുതരും.',
      nameLabel: 'നിങ്ങളുടെ പൂർണ്ണ നാമം',
      companyLabel: 'ബിസിനസ് / കടയുടെ പേര്',
      emailLabel: 'ഇമെയിൽ വിലാസം',
      phoneLabel: 'മൊബൈൽ നമ്പർ (10 അക്കങ്ങൾ)',
      stateLabel: 'സംസ്ഥാനം / ജില്ല',
      cityLabel: 'നഗരം / പ്രദേശം',
      turnoverLabel: 'വാർഷിക വിൽപ്പന / വിറ്റുവരവ്',
      requirementLabel: 'നിങ്ങൾക്ക് എന്തിനാണ് സഹായം വേണ്ടത്?',
      submitButton: 'സൗജന്യ മാർക്കറ്റ് റിപ്പോർട്ട് നേടുക',
      submitting: 'സമർപ്പിക്കുന്നു...',
      formSuccessTitle: 'നന്ദി! അഭ്യർത്ഥന ലഭിച്ചു',
      formSuccessMsg: 'ഞങ്ങളുടെ എംഎസ്എംഇ വളർച്ചാ വിദഗ്ദ്ധർ നിങ്ങളുടെ ജില്ലാ മാർക്കറ്റ് ഡാറ്റ പരിശോധിച്ച് 24 മണിക്കൂറിനുള്ളിൽ ബന്ധപ്പെടും.',
      showcaseHeading: 'ഇന്ററാക്ടീവ് അവസര സാൻഡ്‌ബോക്സ്',
      showcaseSubheading: 'സ്ഥിരീകരിച്ച ബിസിനസ്സ് രേഖകളിൽ നിന്ന് കണക്കാക്കിയ യഥാർത്ഥ വിപണി വിടവുകളും വളർച്ചാ അവസരങ്ങളും കണ്ടെത്തുക.',
      tabRadar: 'വളർച്ചാ റഡാർ',
      tabWhitespace: 'വിപണി വിടവുകൾ',
      tabFastest: 'വേഗത്തിലുള്ള വികസനം',
    },
    search: {
      searchTitle: 'എതിരാളി വിശകലനം',
      pageTitle: 'പ്രാദേശിക ബിസിനസുകളെയും എതിരാളികളെയും കണ്ടെത്തുക',
      pageSubtitle: 'കടകളും ബിസിനസുകളും പരിശോധിക്കുക, നിങ്ങളുടെ വിപണി എത്രത്തോളം മത്സരപരമാണെന്ന് കാണുക, മികച്ച സ്ഥലങ്ങൾ കണ്ടെത്തുക:',
      tabPincode: 'പിൻകോഡ്',
      tabKeyword: 'ബിസിനസ് കീവേഡ്',
      tabSector: 'വ്യവസായം (വിഭാഗം)',
      tabArea: 'പ്രദേശത്ത് തിരയുക',
      pincodeLabel: 'പിൻകോഡ്',
      keywordLabel: 'ബിസിനസ് തരം',
      sectorLabel: 'വ്യവസായ മേഖല',
      searchButton: 'തിരയുക',
      searching: 'തിരയുന്നു...',
      totalBusinesses: 'മൊത്തം ബിസിനസുകൾ',
      competitors: 'അടുത്തുള്ള എതിരാളികൾ',
      competitionLevel: 'മത്സര നിലവാരം',
      saturation: 'വിപണി സാച്ചുറേഷൻ',
      recommendation: 'ഉപദേശവും അവസരങ്ങളും',
      businesses: 'ബിസിനസുകൾ',
      sector: 'മേഖല / പ്രവർത്തന രംഗം',
      location: 'സ്ഥലം',
      registered: 'രജിസ്റ്റർ ചെയ്തത്',
      viewDetails: 'വിശദാംശങ്ങൾ കാണുക',
      viewModeCards: 'കാർഡുകൾ',
      viewModeTable: 'പട്ടിക',
      viewModeMap: 'ഭൂപടം',
      exportCSV: 'CSV ഡൗൺലോഡ്',
      selectedDistrict: 'തിരഞ്ഞെടുത്ത ജില്ല',
      specificNeighborhood: 'പ്രത്യേക പ്രദേശം',
      allNeighborhoods: 'എല്ലാ പ്രദേശങ്ങളും',
      filters: 'ഫിൽട്ടറുകൾ:',
      areaLocality: 'പ്രദേശം / പ്രദേശം',
    },
    analytics: {
      eyebrow: 'തത്സമയ പ്രാദേശിക ബിസിനസ്സ് അവലോകനം',
      heading: 'മാർക്കറ്റ് അനലിറ്റിക്‌സും ബിസിനസ്സ് സാന്ദ്രതയും',
      subheading: 'സ്ഥിരീകരിച്ച ബിസിനസുകൾ, മുൻനിര വ്യവസായങ്ങൾ, പ്രാദേശിക ഹോട്ട്‌സ്‌പോട്ടുകൾ എന്നിവ പര്യവേക്ഷണം ചെയ്യുക:',
      categoriesPill: 'ബിസിനസ്സ് വിഭാഗങ്ങൾ',
      pincodesPill: 'പിൻകോഡ് പ്രദേശങ്ങൾ',
      topSharePill: 'ഏറ്റവും വലിയ വ്യവസായ വിഹിതം',
      scaleEyebrow: 'ബിസിനസ്സ് വലുപ്പങ്ങളും വിഭാഗങ്ങളും',
      scaleTitle: 'ബിസിനസ്സ് വലുപ്പങ്ങളുടെ വിതരണം',
      microDesc: 'മൈക്രോ (₹1 കോടി വരെ യന്ത്രങ്ങൾ / ₹5 കോടി വിൽപ്പന)',
      smallDesc: 'സ്മോൾ (₹10 കോടി വരെ യന്ത്രങ്ങൾ / ₹50 കോടി വിൽപ്പന)',
      mediumDesc: 'മീഡിയം (₹50 കോടി വരെ യന്ത്രങ്ങൾ / ₹250 കോടി വിൽപ്പന)',
      heatmapTitle: 'പ്രാദേശിക ബിസിനസ്സ് സാന്ദ്രതാ ഹീറ്റ്‌മാപ്പ്',
      filterPlaceholder: 'വിഭാഗങ്ങൾ ഫിൽട്ടർ ചെയ്യുക...',
      // Deep Analytics Keys
      dimPincode: 'പിൻകോഡുകൾ',
      dimPincodeDesc: 'ബിസിനസുകൾ സ്ഥിതിചെയ്യുന്ന പ്രദേശങ്ങൾ',
      dimSection: 'പ്രധാന വ്യവസായം',
      dimSectionDesc: 'വിശാലമായ മേഖലകൾ (നിർമ്മാണം, റീട്ടെയിൽ മുതലായവ)',
      dimDivision: 'ബിസിനസ്സ് വിഭാഗം',
      dimDivisionDesc: 'വ്യവസായ തരങ്ങൾ (ഭക്ഷണം, വസ്ത്രം, ഐടി)',
      dimGroup: 'ഉപവിഭാഗം',
      dimGroupDesc: 'പ്രത്യേക ബിസിനസ്സ് ഗ്രൂപ്പുകൾ',
      dimClass: 'വ്യാപാര തരം',
      dimClassDesc: 'പ്രത്യേക വ്യാപാരങ്ങളും സേവനങ്ങളും',
      dimSubclass: 'നിർദ്ദിഷ്ട ബിസിനസ്സ്',
      dimSubclassDesc: 'കൃത്യമായ വ്യാപാര ഉൽപ്പാദന പ്രവർത്തനങ്ങൾ',
      nicSection: 'പ്രധാന വ്യവസായം',
      nicDivision: 'ബിസിനസ്സ് വിഭാഗം',
      nicGroup: 'ഉപവിഭാഗം',
      nicClass: 'വ്യാപാര തരം',
      nicSubclass: 'നിർദ്ദിഷ്ട ബിസിനസ്സ്',
      microTitle: 'മൈക്രോ സംരംഭങ്ങൾ',
      smallTitle: 'ചെറുകിട സംരംഭങ്ങൾ',
      mediumTitle: 'ഇടത്തരം സംരംഭങ്ങൾ',
      microCaption: 'ഈടില്ലാത്ത വായ്പകൾക്കും (CGTMSE) മുൻഗണനാ വായ്പാ പദ്ധതികൾക്കും അർഹതയുണ്ട്',
      smallCaption: 'സാങ്കേതികവിദ്യ നവീകരണ ഗ്രാന്റുകൾക്കും കുറഞ്ഞ പലിശ വായ്പകൾക്കും അർഹതയുണ്ട്',
      mediumCaption: 'പ്രാദേശിക വിതരണക്കാരിൽ നിന്ന് വാങ്ങുന്ന വലിയ കമ്പനികൾ',
      signalsEyebrow: 'വിപണി അവസരങ്ങളും മുന്നറിയിപ്പുകളും',
      signalsTitle: 'പ്രധാന വിപണി സൂചനകളും വളർച്ചാ അവസരങ്ങളും',
      signalsDesc: 'ഉപഭോക്തൃ ഡിമാൻഡ് എവിടെയാണ് വർദ്ധിക്കുന്നത്, എവിടെയാണ് മത്സരമില്ലാത്തത് എന്ന് വ്യക്തമാക്കുന്ന ട്രെൻഡുകൾ.',
      signalFilterAll: 'എല്ലാ സിഗ്നലുകളും',
      signalFilterDemand: 'ഉയർന്ന ഡിമാൻഡ്',
      signalFilterWhitespace: 'മത്സരമില്ലാത്ത വിപണികൾ',
      signalFilterHubs: 'പ്രാദേശിക ബിസിനസ്സ് ഹബ്ബുകൾ',
      thSignalType: 'സിഗ്നൽ തരം',
      thCategoryArea: 'ബിസിനസ്സ് വിഭാഗവും പ്രദേശവും',
      thTotalBiz: 'ആകെ ബിസിനസുകൾ',
      thGrowthSpeed: 'വളർച്ചാ വേഗത',
      thConfidence: 'വിശ്വാസ്യത',
      thOpportunity: 'ശുപാർശ ചെയ്ത അവസരം',
      thWhyMatters: 'എന്തുകൊണ്ട് ഇത് പ്രധാനം',
      whyThisBtn: 'ഇതെന്തുകൊണ്ട്?',
      heatmapEyebrow: 'പ്രാദേശിക & വ്യവസായ സാന്ദ്രത',
      heatmapDesc: 'ബിസിനസുകൾ എവിടെയാണ് കൂടുതലായി കേന്ദ്രീകരിച്ചിരിക്കുന്നതെന്ന് (കടും നീല) മനസ്സിലാക്കുക.',
      heatmapPinGuide: 'തപാൽ വിതരണ പ്രദേശം അടിസ്ഥാനമാക്കിയുള്ള ബിസിനസ്സ് സാന്ദ്രത. കടും നീല കാർഡുകൾ ഉയർന്ന മത്സരത്തെ കാണിക്കുന്നു.',
      heatmapNicGuide: 'നാഷണൽ ഇൻഡസ്ട്രിയൽ ക്ലാസിഫിക്കേഷൻ (NIC) വ്യാപാര പ്രവർത്തനങ്ങളും മേഖലകളും.',
      emptyHeatmapTitle: 'പൊരുത്തപ്പെടുന്ന പ്രദേശങ്ങളോ മേഖലകളോ ഇല്ല',
      emptyHeatmapDesc: 'നിങ്ങളുടെ തിരയലുമായി പൊരുത്തപ്പെടുന്നതൊന്നും കണ്ടെത്താനായില്ല. മറ്റൊരു പിൻകോഡ് ഉപയോഗിക്കുക.',
      hierarchyEyebrow: 'വ്യവസായം അടിസ്ഥാനമാക്കിയുള്ള തിരച്ചിൽ',
      hierarchyTitle: 'വ്യവസായ വിഭാഗങ്ങൾ അടിസ്ഥാനമാക്കി ബിസിനസുകൾ ബ്രൗസ് ചെയ്യുക',
      hierarchyDesc: 'കൃത്യമായ വ്യാപാര തരങ്ങളിലേക്ക് എത്തിച്ചേരാൻ ഓരോ വിഭാഗത്തിലും ക്ലിക്ക് ചെയ്യുക.',
      currentLevelLabel: 'നിലവിലെ തലം:',
      allIndustriesRoot: 'എല്ലാ വ്യവസായങ്ങളും',
      clickDrillDown: 'ഈ വിഭാഗത്തിലെ കൃത്യമായ വ്യാപാരങ്ങൾ കാണാൻ ക്ലിക്ക് ചെയ്യുക',
      terminalLevel: 'നിർദ്ദിഷ്ട ബിസിനസ്സ് (കൃത്യമായ വ്യാപാരം)',
      shareOfCategory: 'ഈ വിഭാഗത്തിന്റെ വിഹിതം',
      radarEyebrow: 'ബിസിനസ്സ് അവസരങ്ങൾ കണ്ടെത്തൽ',
      radarTitle: 'നിങ്ങളുടെ ബിസിനസ്സിനായുള്ള 4 പ്രധാന വിപണി അവസരങ്ങൾ',
      q1Title: 'അതിവേഗം വളരുന്ന വ്യവസായങ്ങൾ',
      q1Sub: 'ഉയർന്ന ഉപഭോക്തൃ ഡിമാൻഡും പുതിയ ബിസിനസുകളും',
      q2Title: 'മത്സരമില്ലാത്ത വിപണികൾ (സപ്ലൈ വിടവുകൾ)',
      q2Sub: 'ഉയർന്ന ഡിമാൻഡും കുറഞ്ഞ എതിരാളികളും',
      q3Title: 'തുറന്ന ബിസിനസ്സ് കേന്ദ്രങ്ങൾ',
      q3Sub: 'തിരക്കേറിയ പിൻകോഡുകളും കുറഞ്ഞ എതിരാളികളും',
      q4Title: 'കുതിച്ചുയരുന്ന പ്രാദേശിക ഹബ്ബുകൾ',
      q4Sub: 'വേഗത്തിൽ പുതിയ ബിസിനസുകൾ ആരംഭിക്കുന്ന മേഖലകൾ',
      thisYear: 'ഈ വർഷം',
      totalShops: 'ആകെ കടകൾ',
      growth: 'വളർച്ച',
    },
    trends: {
      eyebrow: 'ബിസിനസ്സ് വളർച്ചാ ട്രാക്കർ · വാർഷിക രേഖകൾ',
      heading: 'മാർക്കറ്റ് ട്രെൻഡുകളും ബിസിനസ്സ് വളർച്ചയും',
      subheading: 'ഓരോ വർഷവും എത്ര പുതിയ ബിസിനസുകൾ ആരംഭിക്കുന്നുവെന്നും ഏതെല്ലാം മേഖലകളാണ് അതിവേഗം വളരുന്നതെന്നും കാണുക:',
      timePeriod: 'കാലയളവ്',
      selectedArea: 'തിരഞ്ഞെടുത്ത പ്രദേശം',
      fastSlowHeader: 'വേഗതയേറിയതും മന്ദഗതിയിലുള്ളതുമായ വളർച്ചാ മേഖലകൾ',
      fastSlowNote: 'ഏതൊക്കെ തരത്തിലുള്ള ബിസിനസുകളാണ് അതിവേഗം വികസിക്കുന്നതെന്ന് കാണുക.',
      annualTrends: 'വാർഷിക രജിസ്ട്രേഷൻ ട്രെൻഡുകൾ',
      keyShifts: 'പ്രധാന വിപണി മാറ്റങ്ങൾ',
    },
    forecast: {
      forecastTitle: 'മാർക്കറ്റ് പ്രവചനവും പാതയും (2 വർഷം)',
      pincodeForecasts: 'പിൻകോഡ് വളർച്ചാ പ്രവചനം',
      sectorForecasts: 'മേഖല മത്സര പ്രവചനം',
      year: 'വർഷം',
      predicted: 'പ്രവചിച്ച എണ്ണം',
      confidence: 'ആത്മവിശ്വാസം',
      high: 'ഉയർന്ന ആത്മവിശ്വാസം (ശക്തമായ റെക്കോർഡ്)',
      medium: 'ഇടത്തരം ആത്മവിശ്വാസം (സാധാരണ റെക്കോർഡ്)',
      low: 'കുറഞ്ഞ ആത്മവിശ്വാസം (പ്രാഥമിക കണക്കുകൂട്ടൽ)',
      recommendation: 'തന്ത്രപരമായ പ്രവേശന നിർദ്ദേശം',
      interpretation: 'മാക്രോ ഡ്രൈവർ വിശകലനം',
      action: 'ശുപാർശ ചെയ്ത നടപടി',
      enter: 'അനുകൂല പ്രവേശന സമയം',
      wait: 'മാർക്കറ്റ് മാറ്റങ്ങൾ നിരീക്ഷിക്കുക',
      avoid: 'ഉയർന്ന സാച്ചുറേഷൻ റിസ്ക് - ജാഗ്രത',
      impact: 'മാർക്കറ്റ് ഇംപാക്ട്',
    },
    profile: {
      title: 'ബിസിനസ്സ് പ്രൊഫൈലും തീരുമാന കോക്ക്പിറ്റും',
      subtitle: 'രജിസ്റ്റർ ചെയ്ത എതിരാളികളുമായി നിങ്ങളുടെ ബിസിനസ്സ് താരതമ്യം ചെയ്യുക, വിപുലീകരണ അവസരങ്ങൾ കണ്ടെത്തുക.',
      operatingDistrict: 'പ്രവർത്തിക്കുന്ന ജില്ല',
      operatingPin: 'പിൻകോഡ്',
      industryCategory: 'വ്യവസായം / ബിസിനസ്സ് വിഭാഗം',
      competitorsTitle: 'അടുത്തുള്ള എതിരാളികൾ',
      growthTitle: 'ജില്ലാ വാർഷിക വളർച്ച',
      saturationTitle: 'വിപണി സാച്ചുറേഷൻ നില',
      saveProfile: 'ബിസിനസ്സ് വിശദാംശങ്ങൾ സംരക്ഷിക്കുക',
      resetDefaults: 'പുനഃക്രമീകരിക്കുക',
      // Deep Profile Keys
      pageEyebrow: 'എന്റെ ബിസിനസ്സ് പ്രൊഫൈലും വളർച്ചാ ഡാഷ്‌ബോർഡും',
      activeReady: 'സജീവം & തയ്യാറാണ്',
      healthScoreBadge: 'ആരോഗ്യ സ്കോർ',
      businessSizeLabel: 'ബിസിനസ്സ് വലുപ്പം',
      locationLabel: 'സ്ഥലം',
      cockpitTitle: 'നിങ്ങളുടെ ബിസിനസ്സ് വിവരങ്ങൾ',
      editDetailsBtn: 'ബിസിനസ്സ് വിവരങ്ങൾ തിരുത്തുക ✎',
      hideDetailsBtn: 'വിവരങ്ങൾ മറയ്ക്കുക',
      quickScaleLabel: 'ബിസിനസ്സ് വലുപ്പം',
      districtLabel: 'ജില്ല',
      pincodeLabel: 'പിൻകോഡ്',
      industryLabel: 'വ്യവസായം / ബിസിനസ്സ് വിഭാഗം',
      pincodePlaceholder: '6-അക്ക പിൻകോഡ്',
      selectIndustryDefault: 'വ്യവസായ വിഭാഗം തിരഞ്ഞെടുക്കുക...',
      businessNameLabel: 'ബിസിനസ്സ് പേര്',
      businessNamePlaceholder: 'നിങ്ങളുടെ കടയുടെ അല്ലെങ്കിൽ ബ്രാൻഡിന്റെ പേര്',
      ownerLabel: 'ഉടമ / ബന്ധപ്പെടേണ്ട വ്യക്തി',
      ownerPlaceholder: 'ഉടമയുടെ പേര്',
      emailLabel: 'ഇമെയിൽ വിലാസം',
      emailPlaceholder: 'name@company.com',
      turnoverLabel: 'വാർഷിക വിൽപ്പന / വിറ്റുവരവ്',
      privacyNotice: 'നിങ്ങളുടെ വിവരങ്ങൾ നിങ്ങളുടെ ഉപകരണത്തിൽ സുരക്ഷിതമായിരിക്കും. ഞങ്ങൾ പങ്കിടില്ല.',
      cancelBtn: 'റദ്ദാക്കുക',
      saveDetailsBtn: 'വിവരങ്ങൾ സംരക്ഷിക്കുക',
      healthCardTitle: 'ബിസിനസ്സ് ആരോഗ്യവും കരുത്തും',
      healthCardDesc: 'പ്രാദേശിക മത്സരം, വിപണി വളർച്ച, സർക്കാർ പദ്ധതികളുടെ ലഭ്യത എന്നിവയെ അടിസ്ഥാനമാക്കിയുള്ള റേറ്റിംഗ്.',
      whyScoreBtn: 'ഈ സ്കോർ എന്തുകൊണ്ട്? ⓘ',
      overallRatingChip: 'മൊത്തത്തിലുള്ള റേറ്റിംഗ്',
      scoreGradeTag: 'ആരോഗ്യ നിലവാരം',
      riskCaption: 'നിങ്ങളുടെ മേഖലയിലെ ഔദ്യോഗിക സർക്കാർ ഡാറ്റ അടിസ്ഥാനമാക്കി',
      keyRecommendationTitle: 'നിങ്ങളുടെ ബിസിനസ്സിനായുള്ള പ്രധാന ശുപാർശ:',
      factorMarketGrowth: 'വിപണി വളർച്ച',
      factorSpaceRivals: 'എതിരാളികളിൽ നിന്നുള്ള അകലം',
      factorSizeSupport: 'ബിസിനസ്സ് വലുപ്പ പിന്തുണ',
      factorLocalHubs: 'പ്രാദേശിക വിപണി ഹബ്ബുകൾ',
      detailsLink: 'വിശദാംശങ്ങൾ >',
      healthTierStrong: 'ശക്തവും സുരക്ഷിതവും',
      healthTierHealthy: 'ആരോഗ്യകരമായ വളർച്ച',
      healthTierAttention: 'ശ്രദ്ധിക്കേണ്ടതുണ്ട്',
      riskLow: 'കുറഞ്ഞ മത്സര സാധ്യത',
      riskModerate: 'ഇടത്തരം മത്സരം',
      riskHigh: 'ഉയർന്ന വിപണി മത്സരം',
      benchmarkTitle: 'അടുത്തുള്ള ബിസിനസുകളുമായി താരതമ്യം ചെയ്യുക',
      benchmarkDesc: 'നിങ്ങളുടെ പ്രദേശം ജില്ലാ ശരാശരിയുമായി എങ്ങനെ ഒത്തുപോകുന്നുവെന്ന് കാണുക.',
      whyNumbersBtn: 'ഈ നമ്പറുകൾ എന്തുകൊണ്ട്? >',
      tableMetricCol: 'പ്രകടന മെട്രിക്',
      tableYourBizCol: 'നിങ്ങളുടെ പ്രദേശം (PIN)',
      tableDistrictAvgCol: 'ജില്ലാ ശരാശരി',
      tableNationalAvgCol: 'ദേശീയ ശരാശരി',
      tableAssessmentCol: 'വിപണി വിലയിരുത്തൽ',
      seeCompetitorsBtn: 'ഈ പ്രദേശത്തെ എതിരാളികളെ കാണുക →',
      seeForecastBtn: '2-വർഷത്തെ വളർച്ചാ സാധ്യത കാണുക →',
      schemesHeading: 'നിങ്ങൾക്കായുള്ള സർക്കാർ പദ്ധതികളും സബ്‌സിഡികളും',
      schemesSubtitle: 'ഈടില്ലാത്ത വായ്പകൾ, മൂലധന സബ്‌സിഡികൾ, സാങ്കേതിക ഗ്രാന്റുകൾ, സർക്കാർ സംഭരണ മുൻഗണനകൾ.',
      schemesSearchPlaceholder: 'കീവേഡ് വഴി 26 പദ്ധതികൾ തിരയുക (ഉദാ: വായ്പ, സബ്‌സിഡി, സോളാർ, ടെക്സ്റ്റൈൽ, ഫുഡ്)...',
      scaleToggleMatched: 'യോജിച്ച പദ്ധതികൾ',
      scaleToggleAll: 'എല്ലാ ബിസിനസ്സ് വലുപ്പങ്ങളും',
      filterAll: 'എല്ലാ പദ്ധതികളും',
      filterCredit: 'വായ്പകളും ക്രെഡിറ്റും',
      filterSubsidy: 'സബ്‌സിഡികളും ഗ്രാന്റുകളും',
      filterQuality: 'ഗുണനിലവാരം, സാങ്കേതികവിദ്യ & ഇഎസ്ജി',
      filterMarket: 'സർക്കാർ ടെൻഡറുകളും കയറ്റുമതിയും',
      filterSpecial: 'കരകൗശല വിദഗ്ദ്ധർ, വനിതകൾ & ഹരിതം',
      emptySchemesTitle: 'പൊരുത്തപ്പെടുന്ന പദ്ധതികൾ കണ്ടെത്താനായില്ല',
      emptySchemesDesc: 'മറ്റൊരു വാക്ക് തിരയുക അല്ലെങ്കിൽ "എല്ലാ ബിസിനസ്സ് വലുപ്പങ്ങളും" തിരഞ്ഞെടുക്കുക.',
      resetFiltersBtn: 'ഫിൽട്ടറുകൾ മാറ്റി 26 പദ്ധതികളും കാണുക',
      matchLabel: 'പൊരുത്തം',
      eligibleLabel: 'അർഹത:',
      checkEligibilityBtn: 'അർഹതയും വിവരങ്ങളും പരിശോധിക്കുക →',
      officialPortalLink: 'ഔദ്യോഗിക സർക്കാർ പോർട്ടൽ ↗',
      modalFinancialSupport: 'സാമ്പത്തിക സഹായം / പരിധി',
      modalNodalAgency: 'നോഡൽ മന്ത്രാലയം / ഏജൻസി',
      modalTargetSizes: 'ലക്ഷ്യമിടുന്ന ബിസിനസ്സ് വലുപ്പങ്ങൾ',
      modalAboutTitle: 'പദ്ധതിയെക്കുറിച്ച്',
      modalBenefitsTitle: 'നിങ്ങൾക്ക് ലഭിക്കുന്ന ആനുകൂല്യങ്ങളും സബ്‌സിഡികളും',
      modalEligibilityTitle: 'ആർക്കൊക്കെ അർഹതയുണ്ട്',
      modalDocumentsTitle: 'ആവശ്യമായ പ്രധാന രേഖകൾ (ലിസ്റ്റ്)',
      modalApplyBtn: 'ഔദ്യോഗിക സർക്കാർ പോർട്ടലിൽ അപേക്ഷിക്കുക ↗',
      modalCloseBtn: 'അടയ്ക്കുക',
      expansionTitle: 'വികസിപ്പിക്കാൻ അനുയോജ്യമായ സമീപ പ്രദേശങ്ങൾ',
      expansionSubtitle: 'കുറഞ്ഞ മത്സരവും ഉയർന്ന ഡിമാൻഡുമുള്ള സമീപ പ്രദേശങ്ങൾ.',
      expansionBadge: 'കുറഞ്ഞ മത്സരമുള്ള പ്രദേശങ്ങൾ:',
      emptyExpansionTitle: 'അനുയോജ്യമായ പ്രദേശങ്ങൾ കണ്ടെത്തിയില്ല',
      emptyExpansionDesc: 'മറ്റൊരു ജില്ലയോ വിഭാഗമോ തിരഞ്ഞെടുക്കുക.',
      openSpaceLabel: 'തുറന്ന വിപണി സാധ്യത',
      existingCompetitorsLabel: 'നിലവിലുള്ള എതിരാളികൾ',
      totalShopsLabel: 'പ്രദേശത്തെ ആകെ കടകൾ',
      explorePinBtn: 'ഈ പിൻകോഡ് പരിശോധിക്കുക →',
      playbookTitle: 'സർക്കാർ ആനുകൂല്യങ്ങളും നിയമപരമായ അവകാശങ്ങളും',
      playbookSubtitle: '45 ദിവസത്തിനകം പണം ലഭിക്കുന്നതിനുള്ള നിയമപരമായ പരിരക്ഷയും കുറഞ്ഞ പലിശ വായ്പകളും.',
      playbookBadge: 'ഔദ്യോഗിക അവകാശങ്ങളും പദ്ധതികളും',
      alertsTitle: 'വിപണി അലേർട്ടുകളും അറിയിപ്പുകളും',
      alertsActive: 'അലേർട്ടുകൾ സജീവമാണ്',
      alertsSubtitle: 'പുതിയ എതിരാളികൾ വരുമ്പോഴോ വിപണി മാറുമ്പോഴോ അറിയിപ്പുകൾ നേടുക.',
      savedSearchesTab: 'സംരക്ഷിച്ച തിരയലുകളും പ്രദേശങ്ങളും',
      watchedBizTab: 'നിരീക്ഷിക്കുന്ന ബിസിനസുകൾ',
      bookmarkCurrentAreaBtn: 'ഈ പ്രദേശം സംരക്ഷിക്കുക',
      viewModeTitle: 'സ്ക്രീൻ വ്യൂ മോഡ്',
      comfortableMode: 'വിശാലമായത് (സാധാരണ)',
      compactMode: 'ഒതുക്കമുള്ളത് (കൂടുതൽ ഡാറ്റ)',
      resetAllSettingsBtn: 'എല്ലാ ക്രമീകരണങ്ങളും മാറ്റുക',
      saveSettingsBtn: 'ക്രമീകരണങ്ങൾ സംരക്ഷിക്കുക',
      savingBtn: 'സംരക്ഷിക്കുന്നു...',
    },
  },

  hi: {
    common: {
      micro: 'माइक्रो',
      small: 'लघु',
      medium: 'मध्यम',
      enterprises: 'उद्यम',
      businesses: 'व्यवसाय',
      shops: 'दुकानें',
      all: 'सभी',
      search: 'खोजें',
      close: 'बंद करें',
      save: 'सहेजें',
      cancel: 'रद्द करें',
      reset: 'रीसेट',
      loading: 'लोड हो रहा है...',
      active: 'सक्रिय',
      details: 'विवरण',
      explore: 'अन्वेषण करें',
      verified: 'सत्यापित',
      status: 'स्थिति',
    },
    nav: {
      home: 'अवलोकन',
      analytics: 'एनालिटिक्स',
      search: 'खोजें',
      trends: 'रुझान',
      forecast: 'दृष्टिकोण',
      profile: 'मेरा व्यवसाय',
      appTitle: 'एमएसएमई व्यापार अंतर्दृष्टि',
      brandName: 'LYSARQ',
      brandSub: 'MSME',
      searchPlaceholder: 'पूरे भारत में दुकानें, पिन कोड, व्यापार खोजें...',
      saveView: 'दृश्य सहेजें',
      download: 'डाउनलोड',
      share: 'साझा करें',
      updatingMarket: 'बाज़ार डेटा अपडेट हो रहा है',
      platformVisitors: 'प्लेटफ़ॉर्म आगंतुक',
      languageLabel: 'Language / மொழி / ഭാഷ / भाषा:',
      tools: 'उपकरण',
      allTools: 'सभी उपकरण',
      contactUs: 'हमसे संपर्क करें',
      needAi: 'क्या आपके व्यवसाय को AI की आवश्यकता है?',
    },
    visitor: {
      label: 'कुल आगंतुक',
      short: 'आगंतुक',
      today: 'आज के आगंतुक',
      allTime: 'कुल विज़िट',
      status: 'सत्यापित गणना',
      footer: 'सत्र-सत्यापित एमएसएमई एनालिटिक्स',
    },
    dashboard: {
      heroHeading1: 'नए ग्राहक खोजें और बिक्री बढ़ाएं',
      heroHeading2: 'सरल, सत्यापित बाज़ार डेटा के साथ',
      heroSubheading: 'वास्तविक खरीदार खोजें, नजदीकी प्रतिस्पर्धियों की जांच करें और देखें कि मांग कहाँ बढ़ रही है',
      heroNarrative: 'पूरे भारत में वास्तविक, सत्यापित व्यावसायिक डेटा के साथ अपनी बिक्री बढ़ाएं। चाहे आप एक छोटी कार्यशाला, विनिर्माण संयंत्र, खुदरा दुकान या सेवा एजेंसी चलाते हों, लाइसार्क आपको नए खरीदार खोजने, बिना किसी प्रतिस्पर्धा के उच्च मांग वाले बाज़ार खोजने और तेजी से बढ़ते क्षेत्रों की पहचान करने में मदद करता है। बिना किसी तकनीकी जटिलता के — केवल व्यावहारिक तथ्य जो आपके मुनाफे को बढ़ाएं।',
      verifiedBadge: 'आधिकारिक सरकारी डेटा',
      statRegistered: 'पंजीकृत व्यवसाय',
      statPincodes: 'कवर किए गए पिन कोड',
      statTopSector: 'क्षेत्र का शीर्ष उद्योग',
      statNewShops: 'नए जुड़े व्यवसाय व इकाइयाँ',
      statMarketGaps: 'उच्च मांग वाले बाज़ार अंतराल',
      formTitle: 'अपने व्यवसाय के लिए निःशुल्क बाज़ार रिपोर्ट प्राप्त करें',
      formSubtitle: 'त्वरित 1-मिनट पूछताछ। हम आपको आपके क्षेत्र की बाज़ार रिपोर्ट और नए ग्राहक अवसर भेजेंगे।',
      nameLabel: 'आपका पूरा नाम',
      companyLabel: 'व्यापार / दुकान का नाम',
      emailLabel: 'ईमेल पता',
      phoneLabel: 'मोबाइल नंबर (10 अंक)',
      stateLabel: 'राज्य / ज़िला',
      cityLabel: 'शहर / कस्बा',
      turnoverLabel: 'वार्षिक बिक्री / टर्नओवर',
      requirementLabel: 'आपको किस प्रकार की सहायता चाहिए?',
      submitButton: 'निःशुल्क बाज़ार रिपोर्ट प्राप्त करें',
      submitting: 'सबमिट हो रहा है...',
      formSuccessTitle: 'धन्यवाद! अनुरोध प्राप्त हुआ',
      formSuccessMsg: 'हमारे एमएसएमई विशेषज्ञ आपके ज़िले के बाज़ार डेटा का विश्लेषण करेंगे और 24 घंटों में आपसे संपर्क करेंगे।',
      showcaseHeading: 'इंटरएक्टिव अवसर सैंडबॉक्स',
      showcaseSubheading: 'सत्यापित व्यावसायिक रिकॉर्ड से गणना किए गए वास्तविक बाज़ार अंतराल और विकास के अवसरों की खोज करें।',
      tabRadar: 'विकास रडार',
      tabWhitespace: 'बाज़ार अंतराल',
      tabFastest: 'तेज़ विस्तार',
    },
    search: {
      searchTitle: 'प्रतिस्पर्धी खोज',
      pageTitle: 'स्थानीय व्यवसाय और प्रतिस्पर्धी खोजें',
      pageSubtitle: 'दुकानों और व्यवसायों की जाँच करें, देखें कि बाज़ार कितना भरा हुआ है, और सर्वश्रेष्ठ स्थान खोजें:',
      tabPincode: 'पिन कोड',
      tabKeyword: 'व्यापार कीवर्ड',
      tabSector: 'उद्योग (श्रेणी)',
      tabArea: 'क्षेत्र में खोजें',
      pincodeLabel: 'पिन कोड',
      keywordLabel: 'व्यापार प्रकार',
      sectorLabel: 'उद्योग श्रेणी',
      searchButton: 'खोजें',
      searching: 'खोज जारी है...',
      totalBusinesses: 'कुल दुकानें व व्यवसाय',
      competitors: 'नजदीकी प्रतिस्पर्धी',
      competitionLevel: 'प्रतिस्पर्धा स्तर',
      saturation: 'बाज़ार संतृप्ति',
      recommendation: 'सलाह और अवसर',
      businesses: 'दुकानें और व्यवसाय',
      sector: 'श्रेणी / कार्य क्षेत्र',
      location: 'स्थान',
      registered: 'पंजीकृत तिथि',
      viewDetails: 'पूरा विवरण देखें',
      viewModeCards: 'कार्ड्स',
      viewModeTable: 'तालिका',
      viewModeMap: 'मानचित्र',
      exportCSV: 'सीएसवी निर्यात',
      selectedDistrict: 'चयनित ज़िला',
      specificNeighborhood: 'विशिष्ट इलाका / उप-क्षेत्र',
      allNeighborhoods: 'सभी इलाके',
      filters: 'फ़िल्टर:',
      areaLocality: 'क्षेत्र / इलाका',
    },
    analytics: {
      eyebrow: 'लाइव क्षेत्रीय व्यापार अवलोकन',
      heading: 'बाज़ार विश्लेषण और व्यापार घनत्व',
      subheading: 'सत्यापित व्यवसायों, शीर्ष उद्योगों और स्थानीय पिन कोड केंद्रों का अन्वेषण करें:',
      categoriesPill: 'व्यावसायिक श्रेणियाँ',
      pincodesPill: 'पिन कोड क्षेत्र',
      topSharePill: 'शीर्ष उद्योग हिस्सा',
      scaleEyebrow: 'व्यापार आकार और श्रेणियाँ',
      scaleTitle: 'व्यापार आकार का वितरण',
      microDesc: 'माइक्रो (₹1 करोड़ तक मशीनरी / ₹5 करोड़ बिक्री)',
      smallDesc: 'लघु (₹10 करोड़ तक मशीनरी / ₹50 करोड़ बिक्री)',
      mediumDesc: 'मध्यम (₹50 करोड़ तक मशीनरी / ₹250 करोड़ बिक्री)',
      heatmapTitle: 'क्षेत्रीय व्यापार घनत्व हीटमैप',
      filterPlaceholder: 'श्रेणियाँ या कोड फ़िल्टर करें...',
      // Deep Analytics Keys
      dimPincode: 'पिन कोड',
      dimPincodeDesc: 'व्यवसाय कहाँ स्थित हैं',
      dimSection: 'मुख्य उद्योग',
      dimSectionDesc: 'प्रमुख क्षेत्र (जैसे विनिर्माण, खुदरा)',
      dimDivision: 'व्यावसायिक श्रेणी',
      dimDivisionDesc: 'उद्योग के प्रकार (जैसे खाद्य, वस्त्र, आईटी)',
      dimGroup: 'उप-श्रेणी',
      dimGroupDesc: 'विशिष्ट व्यापार समूह',
      dimClass: 'व्यापार प्रकार',
      dimClassDesc: 'विशिष्ट व्यापार और सेवाएँ',
      dimSubclass: 'विशिष्ट व्यापार',
      dimSubclassDesc: 'सटीक व्यापार व उत्पाद गतिविधियाँ',
      nicSection: 'मुख्य उद्योग',
      nicDivision: 'व्यावसायिक श्रेणी',
      nicGroup: 'उप-श्रेणी',
      nicClass: 'व्यापार प्रकार',
      nicSubclass: 'विशिष्ट व्यापार',
      microTitle: 'माइक्रो व्यवसाय',
      smallTitle: 'लघु उद्यम',
      mediumTitle: 'मध्यम उद्यम',
      microCaption: 'बिना गारंटी बैंक ऋण (CGTMSE) और प्राथमिकता ऋण योजनाओं के लिए पात्र',
      smallCaption: 'प्रौद्योगिकी उन्नयन अनुदान और कम ब्याज ऋण के लिए पात्र',
      mediumCaption: 'प्रमुख क्षेत्रीय नियोक्ता और स्थानीय आपूर्तिकर्ताओं से खरीदने वाली कंपनियाँ',
      signalsEyebrow: 'बाज़ार अवसर और अलर्ट',
      signalsTitle: 'प्रमुख बाज़ार संकेत और विकास के अवसर',
      signalsDesc: 'महत्वपूर्ण रुझान जो दिखाते हैं कि ग्राहकों की मांग कहाँ बढ़ रही है, किन क्षेत्रों में प्रतिस्पर्धा की कमी है।',
      signalFilterAll: 'सभी संकेत',
      signalFilterDemand: 'बढ़ती मांग',
      signalFilterWhitespace: 'शून्य-प्रतिस्पर्धा क्षेत्र',
      signalFilterHubs: 'स्थानीय व्यावसायिक केंद्र',
      thSignalType: 'संकेत प्रकार',
      thCategoryArea: 'व्यावसायिक श्रेणी और क्षेत्र',
      thTotalBiz: 'कुल व्यवसाय',
      thGrowthSpeed: 'विकास गति',
      thConfidence: 'विश्वसनीयता',
      thOpportunity: 'अनुशंसित अवसर',
      thWhyMatters: 'यह क्यों महत्वपूर्ण है',
      whyThisBtn: 'कारण जानें',
      heatmapEyebrow: 'स्थानीय क्षेत्र और उद्योग घनत्व',
      heatmapDesc: 'देखें कि व्यवसाय कहाँ अधिक केंद्रित हैं (गहरा नीला) और कहाँ नए अवसरों की जगह है (हल्का नीला)।',
      heatmapPinGuide: 'पिन कोड क्षेत्र के अनुसार व्यावसायिक घनत्व। गहरा नीला रंग अधिक प्रतिस्पर्धा और हल्का नीला विस्तार का अवसर दिखाता है।',
      heatmapNicGuide: 'राष्ट्रीय औद्योगिक वर्गीकरण (NIC) पंजीकृत व्यापार गतिविधियों और क्षेत्रों का विवरण।',
      emptyHeatmapTitle: 'कोई मेल खाता क्षेत्र या उद्योग नहीं मिला',
      emptyHeatmapDesc: 'आपकी खोज से कोई परिणाम नहीं मिला। कृपया दूसरा पिन कोड या कीवर्ड आज़माएँ।',
      hierarchyEyebrow: 'उद्योग-वार खोज',
      hierarchyTitle: 'उद्योग श्रेणियों के अनुसार व्यवसाय ब्राउज़ करें',
      hierarchyDesc: 'सटीक व्यापार प्रकारों तक पहुँचने के लिए मुख्य श्रेणियों पर क्लिक करें।',
      currentLevelLabel: 'वर्तमान स्तर:',
      allIndustriesRoot: 'सभी उद्योग',
      clickDrillDown: 'इस श्रेणी के विशिष्ट व्यापार देखने के लिए क्लिक करें',
      terminalLevel: 'विशिष्ट व्यापार (सटीक श्रेणी)',
      shareOfCategory: 'इस श्रेणी का हिस्सा',
      radarEyebrow: 'व्यावसायिक अवसरों की खोज',
      radarTitle: 'आपके व्यवसाय के लिए 4 बड़े बाज़ार अवसर',
      q1Title: 'तेज़ी से बढ़ते उद्योग',
      q1Sub: 'उच्च ग्राहक मांग और नए व्यवसायों का खुलना',
      q2Title: 'शून्य-प्रतिस्पर्धा क्षेत्र (आपूर्ति अंतराल)',
      q2Sub: 'उच्च मांग और बहुत कम प्रतिस्पर्धी दुकानें',
      q3Title: 'खुले व्यावसायिक स्थान',
      q3Sub: 'व्यस्त पिन कोड और बहुत कम प्रतिस्पर्धी दुकानें',
      q4Title: 'तेज़ी से उभरते स्थानीय केंद्र',
      q4Sub: 'नए व्यवसायों को तेज़ी से आकर्षित करने वाले क्षेत्र',
      thisYear: 'इस वर्ष',
      totalShops: 'कुल दुकानें',
      growth: 'वृद्धि',
    },
    trends: {
      eyebrow: 'व्यापार वृद्धि ट्रैकर · वार्षिक रिकॉर्ड',
      heading: 'बाज़ार रुझान और व्यापार वृद्धि',
      subheading: 'देखें कि प्रत्येक वर्ष कितने नए व्यवसाय खुलते हैं, कौन से उद्योग फलफूल रहे हैं, और कौन से क्षेत्र तेज़ी से बढ़ रहे हैं:',
      timePeriod: 'समय अवधि',
      selectedArea: 'चयनित क्षेत्र',
      fastSlowHeader: 'सबसे तेज़ और धीमी गति से बढ़ने वाले क्षेत्र',
      fastSlowNote: 'देखें कि कौन से प्रकार के व्यवसाय तेज़ी से बढ़ रहे हैं, और कौन से धीमे हो गए हैं।',
      annualTrends: 'वार्षिक पंजीकरण रुझान',
      keyShifts: 'प्रमुख बाज़ार बदलाव',
    },
    forecast: {
      forecastTitle: 'बाज़ार भविष्य का दृष्टिकोण (2-वर्षीय रुझान)',
      pincodeForecasts: 'पिन कोड विकास पूर्वानुमान',
      sectorForecasts: 'श्रेणी विकास पूर्वानुमान',
      year: 'वर्ष',
      predicted: 'अनुमानित दुकानों की संख्या',
      confidence: 'पूर्वानुमान विश्वसनीयता',
      high: 'उच्च सटीकता (मजबूत ट्रैक रिकॉर्ड)',
      medium: 'मध्यम सटीकता (उचित ट्रैक रिकॉर्ड)',
      low: 'प्रारंभिक अनुमान (सीमित इतिहास)',
      recommendation: 'व्यावसायिक सलाह और समय',
      interpretation: 'यह क्यों हो रहा है',
      action: 'अनुशंसित कदम',
      enter: 'खोलने का अच्छा समय',
      wait: 'प्रतीक्षा करें और देखें (बाज़ार पर नज़र रखें)',
      avoid: 'अत्यधिक भीड़भाड़ (प्रतिस्पर्धा का उच्च जोखिम)',
      impact: 'अपेक्षित बाज़ार प्रभाव',
    },
    profile: {
      title: 'व्यापार प्रोफ़ाइल और निर्णय केंद्र',
      subtitle: 'पंजीकृत प्रतिस्पर्धियों के साथ अपने व्यवसाय की तुलना करें, विस्तार के अवसर खोजें और सरकारी योजनाओं की जानकारी लें।',
      operatingDistrict: 'कार्यशील ज़िला',
      operatingPin: 'पिन कोड',
      industryCategory: 'उद्योग / व्यावसायिक श्रेणी',
      competitorsTitle: 'नजदीकी प्रतिस्पर्धी',
      growthTitle: 'ज़िला वार्षिक वृद्धि',
      saturationTitle: 'बाज़ार संतृप्ति स्तर',
      saveProfile: 'व्यापार संदर्भ सहेजें',
      resetDefaults: 'रीसेट करें',
      // Deep Profile Keys
      pageEyebrow: 'मेरा व्यवसाय प्रोफ़ाइल और विकास डैशबोर्ड',
      activeReady: 'सक्रिय और तैयार',
      healthScoreBadge: 'स्वास्थ्य स्कोर',
      businessSizeLabel: 'व्यापार का आकार',
      locationLabel: 'स्थान',
      cockpitTitle: 'आपका व्यावसायिक विवरण',
      editDetailsBtn: 'विवरण संपादित करें ✎',
      hideDetailsBtn: 'विवरण छिपाएँ',
      quickScaleLabel: 'व्यापार का आकार',
      districtLabel: 'ज़िला',
      pincodeLabel: 'पिन कोड',
      industryLabel: 'उद्योग / व्यावसायिक श्रेणी',
      pincodePlaceholder: '6-अंकीय पिन कोड',
      selectIndustryDefault: 'उद्योग प्रभाग चुनें...',
      businessNameLabel: 'व्यापार का नाम',
      businessNamePlaceholder: 'अपनी दुकान या ब्रांड का नाम दर्ज करें',
      ownerLabel: 'मालिक / संपर्क व्यक्ति',
      ownerPlaceholder: 'मालिक का नाम',
      emailLabel: 'ईमेल पता',
      emailPlaceholder: 'name@company.com',
      turnoverLabel: 'वार्षिक बिक्री / टर्नओवर',
      privacyNotice: 'आपका व्यावसायिक विवरण आपके अपने कंप्यूटर पर सुरक्षित रहता है। हम इसे साझा नहीं करते।',
      cancelBtn: 'रद्द करें',
      saveDetailsBtn: 'विवरण सहेजें',
      healthCardTitle: 'व्यापार स्वास्थ्य और शक्ति स्कोर',
      healthCardDesc: 'स्थानीय प्रतिस्पर्धा, बाज़ार विकास और सरकारी योजनाओं की उपलब्धता के आधार पर समग्र रेटिंग।',
      whyScoreBtn: 'यह स्कोर क्यों? ⓘ',
      overallRatingChip: 'समग्र व्यावसायिक रेटिंग',
      scoreGradeTag: 'स्वास्थ्य स्तर',
      riskCaption: 'आपके व्यापार के लिए आधिकारिक सरकारी रिकॉर्ड पर आधारित',
      keyRecommendationTitle: 'आपके व्यवसाय के लिए प्रमुख अनुशंसा:',
      factorMarketGrowth: 'बाज़ार वृद्धि',
      factorSpaceRivals: 'प्रतिस्पर्धियों से सुरक्षित दूरी',
      factorSizeSupport: 'व्यापार आकार प्राथमिकता',
      factorLocalHubs: 'स्थानीय व्यापारिक केंद्र',
      detailsLink: 'विवरण >',
      healthTierStrong: 'मजबूत और लचीला',
      healthTierHealthy: 'स्वस्थ विकास',
      healthTierAttention: 'सुधार की आवश्यकता',
      riskLow: 'कम प्रतिस्पर्धा जोखिम',
      riskModerate: 'मध्यम प्रतिस्पर्धा',
      riskHigh: 'उच्च बाज़ार प्रतिस्पर्धा',
      benchmarkTitle: 'नजदीकी व्यवसायों के साथ तुलना करें',
      benchmarkDesc: 'देखें कि आपका क्षेत्र ज़िला औसत और राष्ट्रीय आंकड़ों की तुलना में कैसा प्रदर्शन कर रहा है।',
      whyNumbersBtn: 'ये आंकड़े क्यों? >',
      tableMetricCol: 'प्रदर्शन मेट्रिक',
      tableYourBizCol: 'आपका क्षेत्र (PIN)',
      tableDistrictAvgCol: 'ज़िला औसत',
      tableNationalAvgCol: 'राष्ट्रीय औसत',
      tableAssessmentCol: 'बाज़ार मूल्यांकन',
      seeCompetitorsBtn: 'इस क्षेत्र में प्रतिस्पर्धी देखें →',
      seeForecastBtn: '2-वर्षीय विकास दृष्टिकोण देखें →',
      schemesHeading: 'आपके लिए सरकारी योजनाएं और सब्सिडी',
      schemesSubtitle: 'बिना गारंटी के ऋण, पूंजीगत मार्जिन सब्सिडी, प्रौद्योगिकी अनुदान और सरकारी निविदाओं में प्राथमिकताएं।',
      schemesSearchPlaceholder: 'कीवर्ड द्वारा 26 योजनाएं खोजें (उदा: ऋण, सब्सिडी, सौर, वस्त्र, खाद्य, पेटेंट)...',
      scaleToggleMatched: 'अनुकूलित योजनाएं',
      scaleToggleAll: 'सभी व्यापार आकार',
      filterAll: 'सभी योजनाएं',
      filterCredit: 'ऋण और क्रेडिट गारंटी',
      filterSubsidy: 'सब्सिडी और अनुदान',
      filterQuality: 'गुणवत्ता, तकनीक और ईएसजी',
      filterMarket: 'सरकारी टेंडर और निर्यात',
      filterSpecial: 'कारीगर, महिला और हरित उद्योग',
      emptySchemesTitle: 'कोई योजना आपके फ़िल्टर से मेल नहीं खाती',
      emptySchemesDesc: 'व्यापक कीवर्ड से खोजें या "सभी व्यापार आकार" चुनें।',
      resetFiltersBtn: 'फ़िल्टर रीसेट करें और सभी 26 योजनाएँ देखें',
      matchLabel: 'मेल',
      eligibleLabel: 'पात्रता:',
      checkEligibilityBtn: 'पात्रता और विवरण देखें →',
      officialPortalLink: 'आधिकारिक सरकारी पोर्टल ↗',
      modalFinancialSupport: 'वित्तीय सहायता / सीमा',
      modalNodalAgency: 'नोडल मंत्रालय / एजेंसी',
      modalTargetSizes: 'लक्षित व्यावसायिक आकार',
      modalAboutTitle: 'इस योजना के बारे में',
      modalBenefitsTitle: 'आपको मिलने वाले लाभ और सब्सिडी',
      modalEligibilityTitle: 'पात्रता शर्तें',
      modalDocumentsTitle: 'आवश्यक प्रमुख दस्तावेज (चेकलिस्ट)',
      modalApplyBtn: 'आधिकारिक सरकारी पोर्टल पर आवेदन करें ↗',
      modalCloseBtn: 'बंद करें',
      expansionTitle: 'विस्तार के लिए सर्वश्रेष्ठ पड़ोसी क्षेत्र',
      expansionSubtitle: 'सक्रिय बाज़ार और कम प्रतिस्पर्धा वाले नजदीकी पिन कोड।',
      expansionBadge: 'कम प्रतिस्पर्धा वाले क्षेत्र:',
      emptyExpansionTitle: 'आसपास कोई कम प्रतिस्पर्धा वाला क्षेत्र नहीं मिला',
      emptyExpansionDesc: 'विस्तार के अवसर खोजने के लिए दूसरा ज़िला या श्रेणी चुनें।',
      openSpaceLabel: 'खुला बाज़ार स्थान',
      existingCompetitorsLabel: 'मौजूदा प्रतिस्पर्धी',
      totalShopsLabel: 'क्षेत्र में कुल दुकानें',
      explorePinBtn: 'इस पिन कोड का अन्वेषण करें →',
      playbookTitle: 'सरकारी लाभ और कानूनी सुरक्षा',
      playbookSubtitle: '45 दिनों के भीतर लंबित भुगतान की वसूली के नियम, ब्याज छूट और सरकारी खरीद में लाभ।',
      playbookBadge: 'आधिकारिक अधिकार और योजनाएं',
      alertsTitle: 'बाज़ार अलर्ट और सूचनाएं',
      alertsActive: 'अलर्ट सक्रिय हैं',
      alertsSubtitle: 'नए प्रतिस्पर्धी खुलने या बाज़ार की स्थिति बदलने पर सूचना प्राप्त करें।',
      savedSearchesTab: 'सहेजे गए खोज और क्षेत्र',
      watchedBizTab: 'निगरानी में रखे गए व्यवसाय',
      bookmarkCurrentAreaBtn: 'वर्तमान क्षेत्र सहेजें',
      viewModeTitle: 'स्क्रीन दृश्य मोड',
      comfortableMode: 'विशाल (डिफ़ॉल्ट)',
      compactMode: 'संक्षिप्त (अधिक डेटा देखें)',
      resetAllSettingsBtn: 'सभी सेटिंग्स रीसेट करें',
      saveSettingsBtn: 'सेटिंग्स सहेजें',
      savingBtn: 'सहेजा जा रहा है...',
    },
  },
};

/**
 * Zero-Crash Translation Fallback Helper (Production Grade)
 * Automatically falls back to English when a key is missing in the chosen language.
 * Uses a safe ES6 Proxy so any undefined key evaluates to an empty string instead of crashing.
 * 
 * @param {string} language - Language code ('en', 'ta', 'ml', 'hi')
 * @param {string} namespace - Section key ('profile', 'analytics', 'common', 'nav', etc.)
 * @returns {Proxy<Object>} Zero-crash safe translation dictionary
 */
export function getTranslation(language = 'en', namespace = 'common') {
  const langKey = (language && translations[language]) ? language : 'en';
  const targetDict = translations[langKey]?.[namespace] || {};
  const fallbackDict = translations.en?.[namespace] || {};

  return new Proxy(targetDict, {
    get(target, prop) {
      if (typeof prop !== 'string') {
        return target[prop];
      }
      if (prop in target && target[prop] !== undefined && target[prop] !== '') {
        return target[prop];
      }
      if (prop in fallbackDict && fallbackDict[prop] !== undefined && fallbackDict[prop] !== '') {
        return fallbackDict[prop];
      }
      return target[prop] !== undefined ? target[prop] : '';
    }
  });
}

export default translations;
